import * as React from 'react'
import {
    Avatar,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Collapse,
    Dialog,
    DialogContent,
    DialogTitle,
    Drawer,
    Grid,
    IconButton,
    InputAdornment,
    LinearProgress,
    CircularProgress,
    MenuItem,
    Paper,
    Portal,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import StopRoundedIcon from '@mui/icons-material/StopRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import DiamondRoundedIcon from '@mui/icons-material/DiamondRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import ChatRoundedIcon from '@mui/icons-material/ChatRounded'
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded'
import ViewAgendaRoundedIcon from '@mui/icons-material/ViewAgendaRounded'
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded'
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import DriveFileRenameOutlineRoundedIcon from '@mui/icons-material/DriveFileRenameOutlineRounded'
import FolderRoundedIcon from '@mui/icons-material/FolderRounded'
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded'

import { ErrorType, ActionType } from '../types.js'
import { CHAT_LANGUAGE_OPTIONS, getChatLanguageLabel } from '../constants/chatLanguages'
import useAutoTranslate from '../hooks/useAutoTranslate'
import { extractLogMessage, normalizeLocalizedLogMessages } from '../utils/liveLogs'
import { resolveAttackEntryArrivalTime } from '../utils/attackMovementTiming'
import { useSmoothLiveNow } from '../utils/liveClock'
import {
    resolveIncomingKhanTauntCount,
    resolveRecentKhanAttackCount
} from '../utils/khanTauntDetection'
import { resolveRuntimeUptimeMs } from '../utils/runtimeStatus'
import SettingsDrawer from './SettingsDrawer'
import MainLayout from '../layout/MainLayout'
import AdminPage from './AdminPage'
import AttackTemplatesPage from './AttackTemplatesPage'
import DashboardBotCard, { getDashboardActivePluginNames } from './DashboardBotCard'
import BattleReportsPanel from './BattleReportsPanel'
import EventCalendar from './EventCalendar'
import PricingPage from './PricingPage'

const compactFormatter = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1
})

const numberFormatter = new Intl.NumberFormat('en-US')

const formatCompact = value =>
    typeof value === 'number' && Number.isFinite(value) ? compactFormatter.format(value) : value

const formatFull = value =>
    typeof value === 'number' && Number.isFinite(value) ? numberFormatter.format(value) : value

export const DASHBOARD_UNGROUPED_GROUP_ID = '__ungrouped__'

const DASHBOARD_BOT_GROUP_STORAGE_PREFIX = 'ggeDashboardBotGroups:v1'

const getDashboardBotId = user => {
    const rawId = user?.id ?? user?.uuid ?? user?.name
    const id = String(rawId ?? '').trim()
    return id || ''
}

const normalizeDashboardGroupName = (name, fallbackName) => {
    const normalizedName = String(name ?? '').trim()
    return (normalizedName || fallbackName).slice(0, 48)
}

const createDashboardBotGroupId = () =>
    `group-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

const buildNextDashboardGroupName = groups => {
    const existingNames = new Set(groups.map(group => String(group?.name ?? '').trim().toLowerCase()))
    let index = groups.length + 1

    while (existingNames.has(`group ${index}`))
        index += 1

    return `Group ${index}`
}

const getDashboardBotGroupStorageKey = accountInfo => {
    const accountId = String(accountInfo?.uuid ?? accountInfo?.username ?? accountInfo?.email ?? 'local').trim() || 'local'
    return `${DASHBOARD_BOT_GROUP_STORAGE_PREFIX}:${accountId}`
}

export const normalizeDashboardBotGroups = groups => {
    const rawGroups = Array.isArray(groups) ? groups : Array.isArray(groups?.groups) ? groups.groups : []
    const assignedBotIds = new Set()

    return rawGroups
        .map((group, index) => {
            const id = String(group?.id ?? '').trim() || createDashboardBotGroupId()
            const name = normalizeDashboardGroupName(group?.name, `Group ${index + 1}`)
            const userIds = []

            if (Array.isArray(group?.userIds)) {
                group.userIds.forEach(userId => {
                    const normalizedUserId = String(userId ?? '').trim()
                    if (!normalizedUserId || assignedBotIds.has(normalizedUserId))
                        return

                    assignedBotIds.add(normalizedUserId)
                    userIds.push(normalizedUserId)
                })
            }

            return { id, name, userIds }
        })
        .filter(group => group.id !== DASHBOARD_UNGROUPED_GROUP_ID)
}

export const reorderDashboardBotGroups = (groups, sourceGroupId, targetGroupId, placement = 'before') => {
    const normalizedGroups = normalizeDashboardBotGroups(groups)
    const sourceIndex = normalizedGroups.findIndex(group => group.id === sourceGroupId)
    const targetIndex = normalizedGroups.findIndex(group => group.id === targetGroupId)

    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex)
        return normalizedGroups

    const nextGroups = [...normalizedGroups]
    const [movedGroup] = nextGroups.splice(sourceIndex, 1)
    let insertIndex = nextGroups.findIndex(group => group.id === targetGroupId)

    if (insertIndex < 0)
        return normalizedGroups

    if (placement === 'after')
        insertIndex += 1

    nextGroups.splice(insertIndex, 0, movedGroup)
    return nextGroups
}

export const moveDashboardBotGroup = (groups, groupId, offset) => {
    const normalizedGroups = normalizeDashboardBotGroups(groups)
    const sourceIndex = normalizedGroups.findIndex(group => group.id === groupId)
    const targetIndex = sourceIndex + Number(offset)

    if (sourceIndex < 0 || targetIndex < 0 || targetIndex >= normalizedGroups.length)
        return normalizedGroups

    const nextGroups = [...normalizedGroups]
    const [movedGroup] = nextGroups.splice(sourceIndex, 1)
    nextGroups.splice(targetIndex, 0, movedGroup)
    return nextGroups
}

const readDashboardBotGroups = storageKey => {
    if (typeof window === 'undefined')
        return []

    try {
        const rawValue = window.localStorage.getItem(storageKey)
        return normalizeDashboardBotGroups(JSON.parse(rawValue ?? '[]'))
    } catch (error) {
        return []
    }
}

const persistDashboardBotGroups = (storageKey, groups) => {
    if (typeof window === 'undefined')
        return

    try {
        window.localStorage.setItem(storageKey, JSON.stringify(normalizeDashboardBotGroups(groups)))
    } catch (error) {
        // Ignore storage errors in private or restricted browsing contexts.
    }
}

export const buildDashboardBotGroupView = (users, groups, searchTerm = '') => {
    const userRows = Array.isArray(users) ? users : []
    const normalizedGroups = normalizeDashboardBotGroups(groups)
    const normalizedSearchTerm = String(searchTerm ?? '').trim().toLowerCase()
    const usersById = new Map()

    userRows.forEach(user => {
        const userId = getDashboardBotId(user)
        if (userId)
            usersById.set(userId, user)
    })

    const matchesSearch = user => {
        if (!normalizedSearchTerm)
            return true

        return String(user?.name ?? '').toLowerCase().includes(normalizedSearchTerm)
    }

    const assignedUserIds = new Set()
    const groupsWithUsers = normalizedGroups.map(group => {
        const allUsers = group.userIds
            .map(userId => usersById.get(userId))
            .filter(Boolean)

        allUsers.forEach(user => assignedUserIds.add(getDashboardBotId(user)))

        return {
            ...group,
            allUsers,
            users: allUsers.filter(matchesSearch)
        }
    })

    const ungroupedAllUsers = userRows.filter(user => !assignedUserIds.has(getDashboardBotId(user)))

    return {
        groups: groupsWithUsers,
        ungroupedAllUsers,
        ungroupedUsers: ungroupedAllUsers.filter(matchesSearch)
    }
}

const formatPlainWholeNumber = value => {
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue) || numericValue <= 0)
        return '0'

    return String(Math.floor(numericValue))
}

const getFirstFiniteNumber = (...values) => {
    for (const value of values) {
        const numericValue = Number(value)
        if (Number.isFinite(numericValue))
            return numericValue
    }

    return undefined
}

const getFirstPositiveFiniteNumber = (...values) => {
    for (const value of values) {
        const numericValue = Number(value)
        if (Number.isFinite(numericValue) && numericValue > 0)
            return numericValue
    }

    return undefined
}

const formatSkipAmount = value => {
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue) || numericValue <= 0)
        return '0'
    return String(Math.floor(numericValue))
}

const formatDuration = durationMs => {
    if (!Number.isFinite(durationMs) || durationMs <= 0)
        return '00:00:00'

    const totalSeconds = Math.floor(durationMs / 1000)
    const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0')
    const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0')
    const seconds = String(totalSeconds % 60).padStart(2, '0')

    return `${hours}:${minutes}:${seconds}`
}

const formatApprovalTimeLeft = durationMs => {
    if (!Number.isFinite(durationMs) || durationMs <= 0)
        return 'Expired'

    const totalSeconds = Math.floor(durationMs / 1000)
    const days = Math.floor(totalSeconds / (24 * 60 * 60))
    const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    if (days > 0)
        return `${days}z ${hours}h ${minutes}m ${seconds}s`
    if (hours > 0)
        return `${hours}h ${minutes}m ${seconds}s`
    if (minutes > 0)
        return `${minutes}m ${seconds}s`

    return `${seconds}s`
}

const formatAttackTimeLeft = (arrivalTime, now = Date.now()) => {
    const numericArrivalTime = Number(arrivalTime)
    if (!Number.isFinite(numericArrivalTime) || numericArrivalTime <= 0)
        return 'Unknown'

    const remainingMs = numericArrivalTime - Number(now)
    if (remainingMs <= 0)
        return 'Now'

    return formatApprovalTimeLeft(remainingMs)
}

const buildAttackSelectionLabel = attackEntry => {
    const accountName = String(attackEntry?.accountName ?? 'Unknown')
    const attackerName = String(attackEntry?.attackerName ?? 'Unknown')
    const targetCoords = String(attackEntry?.targetCoords ?? '--')
    return `${accountName} <- ${attackerName} (${targetCoords})`
}

const formatAttackLastSeen = timestamp => {
    const parsed = Number(timestamp)
    if (!Number.isFinite(parsed) || parsed <= 0)
        return 'Unknown'

    return new Date(parsed).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
    })
}

const formatChatTime = timestamp => {
    const parsed = Number(timestamp)
    if (!Number.isFinite(parsed) || parsed <= 0)
        return '--:--'

    return new Date(parsed).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
    })
}

const LOG_RESOURCE_LABELS = {
    W: 'Wood',
    S: 'Stone',
    F: 'Food',
    C1: 'Coins',
    C2: 'Rubies',
    A: 'Aquamarine',
    C: 'Coal',
    O: 'Oil',
    G: 'Glass',
    SP: 'Shogun points',
    SM: 'Samurai points',
    I: 'Items'
}

const LOG_RESULT_MESSAGES = {
    MISSING_REQUIRED_BUILDING: 'Action skipped because a required building is missing.',
    ATTACK_TOO_MANY_UNITS: 'Attack was rejected because too many units were selected.',
    MISSING_UNITS: 'Attack could not start because the required units were not available.'
}

const LOG_SKIP_LABELS = {
    MS1: '1 minute skip',
    MS2: '5 minute skip',
    MS3: '10 minute skip',
    MS4: '30 minute skip',
    MS5: '1 hour skip',
    MS6: '5 hour skip',
    MS7: '24 hour skip'
}

const formatLogSkipLabel = skipType =>
    skipType ? `${LOG_SKIP_LABELS[skipType] ?? skipType} (${skipType})` : ''

const formatClassicDaimyoSkipPlan = value => {
    const entries = [...String(value ?? '').matchAll(/\b(primary|secondary):([A-Z0-9]+)x(\d+)/gi)]
        .map(([, label, skipType, amount]) =>
            `${label.toLowerCase()} ${formatLogSkipLabel(skipType)} x${amount}`)

    return entries.join(', ')
}

const formatClassicDaimyoSkipInventory = value => {
    const entries = [...String(value ?? '').matchAll(/\b(MS\d+)=(\d+)/gi)]
        .map(([, skipType, amount]) =>
            `${formatLogSkipLabel(skipType)}: ${amount}`)

    return entries.join(', ')
}

const stripLogPrefix = value =>
    String(value ?? '')
        .replace(/^[\d\s,.:-]+(?=\[)/, '')
        .trim()

const parseLogPairs = value =>
    [...String(value ?? '').matchAll(/\b([A-Z][A-Z0-9]*)=(-?\d+)/g)]
        .map(match => [match[1], Number(match[2])])
        .filter(([, amount]) => Number.isFinite(amount))

const formatLogAmount = value =>
    Number.isFinite(Number(value)) ? numberFormatter.format(Number(value)) : String(value ?? '')

const formatLogPairList = pairs =>
    pairs
        .map(([code, amount]) => `${LOG_RESOURCE_LABELS[code] ?? code} ${formatLogAmount(amount)}`)
        .join(', ')

const humanizeLogCode = value =>
    String(value ?? '')
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase())

const extractFirstCoords = value =>
    String(value ?? '').match(/(\d+:\d+)/)?.[1]

const buildModuleDetail = category =>
    category && category !== 'Attack' ? `Module: ${category}.` : ''

const appendModuleDetail = (detail, category) => {
    const moduleDetail = buildModuleDetail(category)
    if (detail && moduleDetail)
        return `${detail} ${moduleDetail}`
    return detail || moduleDetail
}

const parseAttackLaunchLog = (remaining, category, time) => {
    if (!/^Hitting\b/i.test(remaining))
        return undefined

    const impactMatch = remaining.match(/\b((?:\d+\s*[dhms]\s*)+)\s+till impact\b/i)
    if (!impactMatch)
        return undefined

    const impact = impactMatch[1].replace(/\s+/g, ' ').trim()
    const beforeImpact = remaining.slice(0, impactMatch.index).trim()
    const coordsMatches = [...beforeImpact.matchAll(/\b\d+:\d+\b/g)]
    const targetCoords = coordsMatches.at(-1)?.[0] ?? ''
    const targetPrefix = beforeImpact.replace(/^Hitting(?:\s+target)?\s*/i, '').trim()
    const targetDescriptor = targetCoords ?
        targetPrefix.slice(0, targetPrefix.lastIndexOf(targetCoords)).trim() :
        targetPrefix

    const summary =
        targetCoords && targetDescriptor ? `Attack launched on ${targetDescriptor} at ${targetCoords}.` :
            targetCoords ? `Attack launched at ${targetCoords}.` :
                'Attack launched.'

    return {
        time,
        category: 'Attack',
        tone: resolveAttackLogTone(category),
        summary,
        detail: appendModuleDetail(`Impact in ${impact}.`, category)
    }
}

const ATTACK_LOG_TONE_STYLES = Object.freeze({
    attackLaunchAqua: {
        borderColor: 'rgba(34, 211, 238, 0.5)',
        backgroundColor: 'rgba(34, 211, 238, 0.12)',
        chipColor: '#22d3ee',
        accentColor: '#06b6d4',
        summaryColor: '#cffafe'
    },
    attackLaunchBaron: {
        borderColor: 'rgba(251, 146, 60, 0.5)',
        backgroundColor: 'rgba(251, 146, 60, 0.12)',
        chipColor: '#fb923c',
        accentColor: '#f97316',
        summaryColor: '#ffedd5'
    },
    attackLaunchSamurai: {
        borderColor: 'rgba(244, 114, 182, 0.5)',
        backgroundColor: 'rgba(244, 114, 182, 0.12)',
        chipColor: '#f472b6',
        accentColor: '#ec4899',
        summaryColor: '#fce7f3'
    },
    attackLaunchNomad: {
        borderColor: 'rgba(52, 211, 153, 0.5)',
        backgroundColor: 'rgba(52, 211, 153, 0.12)',
        chipColor: '#34d399',
        accentColor: '#10b981',
        summaryColor: '#d1fae5'
    },
    attackLaunchKhan: {
        borderColor: 'rgba(248, 113, 113, 0.5)',
        backgroundColor: 'rgba(248, 113, 113, 0.12)',
        chipColor: '#f87171',
        accentColor: '#ef4444',
        summaryColor: '#fee2e2'
    },
    attackLaunchBeri: {
        borderColor: 'rgba(196, 181, 253, 0.5)',
        backgroundColor: 'rgba(196, 181, 253, 0.12)',
        chipColor: '#c4b5fd',
        accentColor: '#8b5cf6',
        summaryColor: '#ede9fe'
    },
    attackLaunchFortress: {
        borderColor: 'rgba(96, 165, 250, 0.5)',
        backgroundColor: 'rgba(96, 165, 250, 0.12)',
        chipColor: '#60a5fa',
        accentColor: '#3b82f6',
        summaryColor: '#dbeafe'
    },
    attackLaunchDefault: {
        borderColor: 'rgba(251, 191, 36, 0.55)',
        backgroundColor: 'rgba(251, 191, 36, 0.13)',
        chipColor: '#fbbf24',
        accentColor: '#f59e0b',
        summaryColor: '#fff3bf'
    }
})

const resolveAttackLogTone = category => {
    const normalizedCategory = String(category ?? '').toLowerCase()
    if (/\baqua\b|storm aqua|aqua forts?/.test(normalizedCategory))
        return 'attackLaunchAqua'
    if (/barron|baron|robber|tower/.test(normalizedCategory))
        return 'attackLaunchBaron'
    if (/samurai|daimyo/.test(normalizedCategory))
        return 'attackLaunchSamurai'
    if (/nomad/.test(normalizedCategory))
        return 'attackLaunchNomad'
    if (/khan/.test(normalizedCategory))
        return 'attackLaunchKhan'
    if (/beri|berimond/.test(normalizedCategory))
        return 'attackLaunchBeri'
    if (/fortress|fort/.test(normalizedCategory))
        return 'attackLaunchFortress'

    return 'attackLaunchDefault'
}

const isAttackLogCategory = category =>
    /attack|aqua|barron|baron|samurai|daimyo|nomad|khan|beri|berimond|fortress|fort/i.test(String(category ?? ''))

const HIDDEN_FRIENDLY_LOG_CATEGORIES = new Set([
    'profilestatus',
    'battlereports'
])

const isHiddenFriendlyLogEntry = (category, message) => {
    const normalizedCategory = String(category ?? '').trim().toLowerCase()
    const normalizedMessage = String(message ?? '')

    if (HIDDEN_FRIENDLY_LOG_CATEGORIES.has(normalizedCategory))
        return true

    if (/^Inbox poll\b/i.test(normalizedMessage))
        return true

    if (/own-castle attack report/i.test(normalizedMessage))
        return true

    if (normalizedCategory === 'attack straini / corbi') {
        const isRequestedSpySummary =
            /^Launched spy for\s+(?:Straini|Corbi)\s+\d+:\d+;\s*waiting for success/i.test(normalizedMessage) ||
            /^Spy\s+(?:Straini|Corbi)\s+\d+:\d+\s*=>\s*[\d,.]+\s+soldiers\.?$/i.test(normalizedMessage)
        const isRequestedAttackSummary =
            /^Hitting\s+(?:Straini|Corbi)\s+\d+:\d+\s+with\s+C\d+;.*\btill impact\.?$/i.test(normalizedMessage)
        const isImportantFailure =
            /\b(?:error|failed|fatal|stopped|limit reached|NO_[A-Z0-9_]+)\b/i.test(normalizedMessage)

        return !isRequestedSpySummary && !isRequestedAttackSummary && !isImportantFailure
    }

    return false
}

const humanizeClassicDaimyoReason = value => {
    const normalizedValue = String(value ?? '').trim()
    const reasonMap = {
        COOLING_DOWN: 'Target is still on cooldown.',
        DAIMYO_COOLDOWN: 'Target is still on cooldown.',
        NO_FREE_FLANK: 'No open flank was found on the target.',
        INVALID_POSITION: 'The selected attack position was not accepted.',
        NO_ACTIVE_EVENT: 'The Samurai event is not active right now.',
        SOURCE_CASTLE_NOT_FOUND: 'The source castle could not be found.',
        SOURCE_ATTACK_AREA_NOT_FOUND: 'No valid attack area was found for this target.',
        TARGET_NOT_FOUND: 'The selected target could not be found.',
        COMMANDER_NOT_AVAILABLE: 'No commander is ready yet.'
    }

    return reasonMap[normalizedValue] ?? humanizeLogCode(normalizedValue)
}

function parseClassicDaimyoLog(remaining, category, time) {
    if (/Loaded Classic Daimyo definitions from\s+(.+)/i.test(remaining)) {
        const fileName = remaining.match(/Loaded Classic Daimyo definitions from\s+(.+)/i)?.[1]
        return {
            time,
            category: 'Attack',
            tone: 'info',
            summary: `Classic Daimyo data loaded successfully${fileName ? ` from ${fileName}` : ''}.`,
            detail: appendModuleDetail('', category)
        }
    }

    if (!/^Classic Daimyo\b/i.test(remaining))
        return undefined

    if (/^Classic Daimyo mode started from\s+(\d+:\d+)/i.test(remaining)) {
        const coords = remaining.match(/^Classic Daimyo mode started from\s+(\d+:\d+)/i)?.[1]
        return {
            time,
            category: 'Attack',
            tone: 'info',
            summary: `Classic Daimyo mode started from ${coords}.`,
            detail: appendModuleDetail('', category)
        }
    }

    if (/^Classic Daimyo manual target mode\s+(\d+:\d+)/i.test(remaining)) {
        const coords = remaining.match(/^Classic Daimyo manual target mode\s+(\d+:\d+)/i)?.[1]
        return {
            time,
            category: 'Attack',
            tone: 'info',
            summary: `Manual target mode is active for Classic Daimyo target ${coords}.`,
            detail: appendModuleDetail('', category)
        }
    }

    if (/^Classic Daimyo target\s+/i.test(remaining)) {
        const coords = extractFirstCoords(remaining) ?? 'unknown target'
        const rank = remaining.match(/\brank=(\d+)/i)?.[1]
        const level = remaining.match(/\blevel=(\d+)/i)?.[1]
        const cooldown = remaining.match(/\bcooldown=(\d+)/i)?.[1]
        const detailParts = []
        if (rank)
            detailParts.push(`Rank ${rank}`)
        if (level)
            detailParts.push(`Level ${level}`)
        if (cooldown === '0')
            detailParts.push('Ready to attack')
        else if (cooldown)
            detailParts.push('Still on cooldown')

        return {
            time,
            category: 'Attack',
            tone: 'info',
            summary: `Selected Classic Daimyo target at ${coords}.`,
            detail: appendModuleDetail(detailParts.join('. '), category)
        }
    }

    if (/^Classic Daimyo probe source\s+/i.test(remaining)) {
        const sourceCoords = remaining.match(/^Classic Daimyo probe source\s+(\d+:\d+)/i)?.[1]
        const seedCoords = remaining.match(/\btargetSeed=(\d+:\d+)/i)?.[1]
        const candidatesSection = remaining.match(/\bcandidates=(.+)$/i)?.[1] ?? ''
        const candidateCount = candidatesSection ?
            candidatesSection.split(/\s+\|\s+/).filter(Boolean).length :
            0

        return {
            time,
            category: 'Attack',
            tone: 'info',
            summary: seedCoords ?
                `Checked possible Classic Daimyo targets near ${seedCoords}.` :
                'Checked possible Classic Daimyo targets.',
            detail: appendModuleDetail(
                [
                    sourceCoords ? `Source castle ${sourceCoords}.` : '',
                    candidateCount > 0 ? `Found ${candidateCount} possible target${candidateCount === 1 ? '' : 's'}.` : ''
                ].filter(Boolean).join(' '),
                category
            )
        }
    }

    if (/^Classic Daimyo attack anchor resolved\s+/i.test(remaining)) {
        const coords = extractFirstCoords(remaining) ?? 'unknown target'
        const rank = remaining.match(/\brank=(\d+)/i)?.[1]
        const level = remaining.match(/\blevel=(\d+)/i)?.[1]
        const detailParts = []
        if (rank)
            detailParts.push(`Rank ${rank}`)
        if (level)
            detailParts.push(`Level ${level}`)

        return {
            time,
            category: 'Attack',
            tone: 'info',
            summary: `Final Classic Daimyo attack target set to ${coords}.`,
            detail: appendModuleDetail(detailParts.join('. '), category)
        }
    }

    if (/^Classic Daimyo (?:cra|sca)\s+\{/i.test(remaining)) {
        const targetCoords = remaining.match(/"TA":\[\d+,(\d+),(\d+)/)?.slice(1, 3).join(':')
        return {
            time,
            category: 'Attack',
            tone: 'info',
            summary: targetCoords ?
                `Attack data prepared for Classic Daimyo target ${targetCoords}.` :
                'Attack data prepared for the selected Classic Daimyo target.',
            detail: appendModuleDetail('Technical command details are hidden.', category)
        }
    }

    if (/^Classic Daimyo hit\s+(\d+:\d+)\s+/i.test(remaining)) {
        const coords = remaining.match(/^Classic Daimyo hit\s+(\d+:\d+)\s+/i)?.[1]
        const flank = remaining.match(/\bflank=([a-z]+)/i)?.[1]
        const troops = remaining.match(/\btroops=(\d+)/i)?.[1]
        const impact = remaining.match(/\b([0-9dhms\s]+)\s+till impact/i)?.[1]?.trim()
        const detailParts = []
        if (flank)
            detailParts.push(`Flank ${flank}`)
        if (troops)
            detailParts.push(`${troops} troops`)
        if (impact)
            detailParts.push(`Impact in ${impact}`)

        return {
            time,
            category: 'Attack',
            tone: resolveAttackLogTone(category),
            summary: `Classic Daimyo attack sent to ${coords}.`,
            detail: appendModuleDetail(detailParts.join('. '), category)
        }
    }

    if (/^Sending spy to classic Daimyo\s+(\d+:\d+)/i.test(remaining)) {
        const coords = remaining.match(/^Sending spy to classic Daimyo\s+(\d+:\d+)/i)?.[1]
        return {
            time,
            category: 'Attack',
            tone: 'info',
            summary: `Spy sent to Classic Daimyo target ${coords}.`,
            detail: appendModuleDetail('', category)
        }
    }

    if (/^Spy report\s+(\d+:\d+)\s+free flank=([a-z]+)/i.test(remaining)) {
        const [, coords, flank] = remaining.match(/^Spy report\s+(\d+:\d+)\s+free flank=([a-z]+)/i) ?? []
        return {
            time,
            category: 'Attack',
            tone: 'success',
            summary: `Spy report ready for ${coords}.`,
            detail: appendModuleDetail(`Best flank: ${flank}.`, category)
        }
    }

    if (/^Classic Daimyo cooldown detected\s+(\d+:\d+)/i.test(remaining)) {
        const coords = remaining.match(/^Classic Daimyo cooldown detected\s+(\d+:\d+)/i)?.[1]
        const cooldown = remaining.match(/\bcooldown=(\d+)/i)?.[1]
        const configured = formatClassicDaimyoSkipPlan(remaining.match(/\bconfigured=([^;]+?)\s+inventory=/i)?.[1])
        const inventory = formatClassicDaimyoSkipInventory(remaining.match(/\binventory=(.+)$/i)?.[1])
        const detailParts = []
        if (cooldown)
            detailParts.push(`Cooldown: ${cooldown}.`)
        if (configured)
            detailParts.push(`Configured skips: ${configured}.`)
        if (inventory)
            detailParts.push(`Inventory: ${inventory}.`)

        return {
            time,
            category: 'Attack',
            tone: 'warning',
            summary: `Classic Daimyo target ${coords} is on cooldown.`,
            detail: appendModuleDetail(
                detailParts.length > 0 ?
                    detailParts.join(' ') :
                    'The bot is checking whether time skips can be used.',
                category
            )
        }
    }

    if (/^Classic Daimyo skip phase\s+(\w+)/i.test(remaining)) {
        const skipType = remaining.match(/^Classic Daimyo skip phase\s+(\w+)/i)?.[1]
        const available = remaining.match(/\bavailable=(\d+)/i)?.[1]
        return {
            time,
            category: 'Attack',
            tone: 'info',
            summary: `Checking ${formatLogSkipLabel(skipType)} for the current Classic Daimyo target.`,
            detail: appendModuleDetail(available ? `${available} skip item${available === '1' ? '' : 's'} available.` : '', category)
        }
    }

    if (/^Classic Daimyo using configured\s+(\d+)\s+minute skip\s+\((\w+)\)\s+at\s+(\d+:\d+)/i.test(remaining)) {
        const [, minutes, skipType, coords] = remaining.match(/^Classic Daimyo using configured\s+(\d+)\s+minute skip\s+\((\w+)\)\s+at\s+(\d+:\d+)/i) ?? []
        const cooldown = remaining.match(/\bcooldown=(\d+)/i)?.[1]
        const configuredLeft = remaining.match(/\bconfiguredLeft=(\d+)/i)?.[1]
        const inventory = remaining.match(/\binventory=(\d+)/i)?.[1]
        const detailParts = [`Selected from Camp Bot settings as a ${minutes} minute skip.`]
        if (cooldown)
            detailParts.push(`Cooldown before skip: ${cooldown}.`)
        if (configuredLeft)
            detailParts.push(`Configured uses left before this skip: ${configuredLeft}.`)
        if (inventory)
            detailParts.push(`Inventory before use: ${inventory}.`)

        return {
            time,
            category: 'Attack',
            tone: 'info',
            summary: `Using ${formatLogSkipLabel(skipType)} to clear Classic Daimyo cooldown at ${coords}.`,
            detail: appendModuleDetail(detailParts.join(' '), category)
        }
    }

    if (/^Classic Daimyo using skip\s+(\w+)\s+at\s+(\d+:\d+)/i.test(remaining)) {
        const [, skipType, coords] = remaining.match(/^Classic Daimyo using skip\s+(\w+)\s+at\s+(\d+:\d+)/i) ?? []
        return {
            time,
            category: 'Attack',
            tone: 'info',
            summary: `Used ${formatLogSkipLabel(skipType)} on Classic Daimyo target ${coords}.`,
            detail: appendModuleDetail('', category)
        }
    }

    if (/^Classic Daimyo cooldown after\s+(\w+)\s+(\d+:\d+)/i.test(remaining)) {
        const [, skipType, coords] = remaining.match(/^Classic Daimyo cooldown after\s+(\w+)\s+(\d+:\d+)/i) ?? []
        const cooldown = remaining.match(/\bcooldown=(\d+)/i)?.[1]
        const cleared = cooldown === '0'
        return {
            time,
            category: 'Attack',
            tone: cleared ? 'success' : 'info',
            summary: cleared ?
                `Classic Daimyo cooldown cleared on ${coords} after ${formatLogSkipLabel(skipType)}.` :
                `Cooldown updated after using ${formatLogSkipLabel(skipType)} on ${coords}.`,
            detail: appendModuleDetail(
                cooldown ? `Remaining cooldown: ${cooldown}.` : '',
                category
            )
        }
    }

    if (/^Classic Daimyo skip not applied at\s+(\d+:\d+):\s*([^;]+)/i.test(remaining)) {
        const [, coords, reason] = remaining.match(/^Classic Daimyo skip not applied at\s+(\d+:\d+):\s*([^;]+)/i) ?? []
        const cooldown = remaining.match(/\bcooldown=(\d+)/i)?.[1]
        const configured = formatClassicDaimyoSkipPlan(remaining.match(/\bconfigured=([^;]+?)(?:;|$)/i)?.[1])
        const inventory = formatClassicDaimyoSkipInventory(remaining.match(/\binventory=(.+)$/i)?.[1])
        const detailParts = [`Reason: ${humanizeLogCode(reason)}.`]
        if (cooldown)
            detailParts.push(`Cooldown: ${cooldown}.`)
        if (configured)
            detailParts.push(`Configured skips: ${configured}.`)
        if (inventory)
            detailParts.push(`Inventory: ${inventory}.`)

        return {
            time,
            category: 'Attack',
            tone: 'warning',
            summary: `No skip was used on Classic Daimyo target ${coords}.`,
            detail: appendModuleDetail(detailParts.join(' '), category)
        }
    }

    if (/^Classic Daimyo cooldown remains\s+(\d+:\d+)/i.test(remaining)) {
        const coords = remaining.match(/^Classic Daimyo cooldown remains\s+(\d+:\d+)/i)?.[1]
        return {
            time,
            category: 'Attack',
            tone: 'warning',
            summary: `Classic Daimyo target ${coords} is still on cooldown.`,
            detail: appendModuleDetail('No suitable skip was used, or more waiting time is still needed.', category)
        }
    }

    if (/^Classic Daimyo waiting for recovery/i.test(remaining)) {
        return {
            time,
            category: 'Attack',
            tone: 'info',
            summary: 'Waiting for the commander to become available again.',
            detail: appendModuleDetail('', category)
        }
    }

    if (/^Classic Daimyo return received/i.test(remaining)) {
        return {
            time,
            category: 'Attack',
            tone: 'success',
            summary: 'Commander returned successfully from the Classic Daimyo attack.',
            detail: appendModuleDetail('', category)
        }
    }

    if (/^Classic Daimyo defeat confirmed/i.test(remaining)) {
        return {
            time,
            category: 'Attack',
            tone: 'warning',
            summary: 'Classic Daimyo attack was lost and the commander returned.',
            detail: appendModuleDetail('', category)
        }
    }

    if (/^Classic Daimyo commander recovered/i.test(remaining)) {
        return {
            time,
            category: 'Attack',
            tone: 'success',
            summary: 'Commander is available again after the Classic Daimyo attack.',
            detail: appendModuleDetail('', category)
        }
    }

    if (/^Classic Daimyo recovery wait timed out/i.test(remaining)) {
        return {
            time,
            category: 'Attack',
            tone: 'warning',
            summary: 'The bot is still waiting for the commander to recover.',
            detail: appendModuleDetail('', category)
        }
    }

    if (/^Classic Daimyo (?:gda lookup failed|battle log fetch failed|spy log fetch failed)/i.test(remaining)) {
        return {
            time,
            category: 'Warning',
            tone: 'warning',
            summary: 'Classic Daimyo details could not be read from the game yet.',
            detail: appendModuleDetail('The bot will try again automatically.', category)
        }
    }

    if (/^Classic Daimyo spy log\b/i.test(remaining)) {
        const coords = extractFirstCoords(remaining)
        return {
            time,
            category: 'Attack',
            tone: 'info',
            summary: coords ?
                `Spy report received for Classic Daimyo target ${coords}.` :
                'Spy report received for the Classic Daimyo target.',
            detail: appendModuleDetail('', category)
        }
    }

    if (/^Classic Daimyo spy report remapped\s+(\d+:\d+)\s+->\s+(\d+:\d+)/i.test(remaining)) {
        const [, fromCoords, toCoords] = remaining.match(/^Classic Daimyo spy report remapped\s+(\d+:\d+)\s+->\s+(\d+:\d+)/i) ?? []
        return {
            time,
            category: 'Attack',
            tone: 'info',
            summary: `Spy result was matched from ${fromCoords} to ${toCoords}.`,
            detail: appendModuleDetail('', category)
        }
    }

    if (/^Classic Daimyo spy message\b/i.test(remaining)) {
        return {
            time,
            category: 'Attack',
            tone: 'info',
            summary: 'A Classic Daimyo spy message was received and processed.',
            detail: appendModuleDetail('', category)
        }
    }

    if (/^Classic Daimyo battle log\b/i.test(remaining)) {
        const coords = extractFirstCoords(remaining)
        return {
            time,
            category: 'Attack',
            tone: 'info',
            summary: coords ?
                `Battle report received for Classic Daimyo target ${coords}.` :
                'Battle report received for a Classic Daimyo attack.',
            detail: appendModuleDetail('', category)
        }
    }

    if (/^Classic Daimyo .* rejected\b/i.test(remaining)) {
        const result = remaining.match(/\bresult=([A-Z0-9_]+)/i)?.[1]
        return {
            time,
            category: 'Warning',
            tone: 'warning',
            summary: `A Classic Daimyo target or attack variant was rejected by the game.`,
            detail: appendModuleDetail(result ? `Reason: ${humanizeClassicDaimyoReason(result)}.` : '', category)
        }
    }

    if (/^Classic Daimyo \w+ failed\b/i.test(remaining)) {
        const result = remaining.match(/\bfailed\s+([A-Z0-9_]+)/i)?.[1]
        return {
            time,
            category: 'Warning',
            tone: 'warning',
            summary: 'Classic Daimyo attack preparation failed.',
            detail: appendModuleDetail(result ? `Reason: ${humanizeClassicDaimyoReason(result)}.` : '', category)
        }
    }

    if (/^Classic Daimyo retry reason:\s*(.+)$/i.test(remaining) || /^Classic Daimyo short retry reason:\s*(.+)$/i.test(remaining)) {
        const reason = remaining.match(/reason:\s*(.+)$/i)?.[1]
        return {
            time,
            category: 'Warning',
            tone: 'warning',
            summary: 'Classic Daimyo attack was delayed and will be retried automatically.',
            detail: appendModuleDetail(reason ? `Reason: ${humanizeClassicDaimyoReason(reason)}.` : '', category)
        }
    }

    if (remaining.includes('{') || remaining.includes('extra=[')) {
        const coords = extractFirstCoords(remaining)
        return {
            time,
            category: 'Attack',
            tone: 'info',
            summary: coords ?
                `Classic Daimyo command prepared for ${coords}.` :
                'Classic Daimyo command prepared.',
            detail: appendModuleDetail('Technical details are hidden to keep the log readable.', category)
        }
    }

    return {
        time,
        category: 'Attack',
        tone: 'info',
        summary: 'Classic Daimyo activity updated.',
        detail: appendModuleDetail('', category)
    }
}

const getLogToneStyles = tone => {
    switch (tone) {
        case 'success':
            return {
                borderColor: 'rgba(110, 212, 165, 0.24)',
                backgroundColor: 'rgba(110, 212, 165, 0.08)',
                chipColor: '#6ed4a5'
            }
        case 'warning':
            return {
                borderColor: 'rgba(240, 179, 109, 0.24)',
                backgroundColor: 'rgba(240, 179, 109, 0.08)',
                chipColor: '#f0b36d'
            }
        case 'error':
            return {
                borderColor: 'rgba(244, 132, 132, 0.24)',
                backgroundColor: 'rgba(244, 132, 132, 0.08)',
                chipColor: '#f48484'
            }
        case 'attack':
            return {
                borderColor: 'rgba(111, 157, 255, 0.24)',
                backgroundColor: 'rgba(111, 157, 255, 0.08)',
                chipColor: '#8eb2ff'
            }
        case 'attackLaunch':
            return {
                ...ATTACK_LOG_TONE_STYLES.attackLaunchDefault
            }
        case 'attackLaunchAqua':
        case 'attackLaunchBaron':
        case 'attackLaunchSamurai':
        case 'attackLaunchNomad':
        case 'attackLaunchKhan':
        case 'attackLaunchBeri':
        case 'attackLaunchFortress':
        case 'attackLaunchDefault':
            return ATTACK_LOG_TONE_STYLES[tone]
        case 'defenseSend':
            return {
                borderColor: 'rgba(248, 113, 113, 0.62)',
                backgroundColor: 'rgba(127, 29, 29, 0.24)',
                chipColor: '#fca5a5',
                accentColor: '#ef4444',
                summaryColor: '#fee2e2'
            }
        default:
            return {
                borderColor: 'rgba(255,255,255,0.08)',
                backgroundColor: 'rgba(255,255,255,0.03)',
                chipColor: '#b8c7e6'
            }
    }
}

export function buildFriendlyLogEntry(message) {
    const raw = stripLogPrefix(extractLogMessage(message))
    if (!raw)
        return undefined

    const timeMatch = raw.match(/^\[(\d{1,2}:\d{2}(?::\d{2})?)\]\s*(.*)$/)
    const time = timeMatch?.[1] ?? ''
    let remaining = (timeMatch?.[2] ?? raw).trim()
    const tags = []

    while (remaining.startsWith('[')) {
        const tagMatch = remaining.match(/^\[([^\]]+)\]\s*/)
        if (!tagMatch)
            break

        tags.push(tagMatch[1])
        remaining = remaining.slice(tagMatch[0].length).trim()
    }

    const category = tags[0] ?? 'System'
    const secondaryTag = tags[1] ?? ''

    if (category === 'RAW_BATTLE_LOG' || category === 'WS' || isHiddenFriendlyLogEntry(category, remaining))
        return undefined

    const classicDaimyoEntry = parseClassicDaimyoLog(remaining, category, time)
    if (classicDaimyoEntry)
        return classicDaimyoEntry

    if (/No active Samurai event detected on load/i.test(remaining)) {
        return {
            time,
            category: 'Attack',
            tone: 'warning',
            summary: 'Samurai event is not active right now, so no Samurai attacks can be sent.',
            detail: appendModuleDetail('', category)
        }
    }

    if (/Indexed\s+(\d+)\s+owned assets?\s+and\s+(\d+)\s+coordinates/i.test(remaining)) {
        const [, assets, coords] = remaining.match(/Indexed\s+(\d+)\s+owned assets?\s+and\s+(\d+)\s+coordinates/i) ?? []
        return {
            time,
            category: 'System',
            tone: 'info',
            summary: `Loaded ${assets} owned location${assets === '1' ? '' : 's'} and ${coords} saved coordinate${coords === '1' ? '' : 's'} for tracking.`,
            detail: appendModuleDetail('', category)
        }
    }

    if (/Monitoring\s+(\d+)\s+kingdoms?:\s*(.+)$/i.test(remaining)) {
        const [, kingdomCount, kingdomList] = remaining.match(/Monitoring\s+(\d+)\s+kingdoms?:\s*(.+)$/i) ?? []
        const prettyKingdoms = String(kingdomList ?? '')
            .split(',')
            .map(item => item.trim())
            .filter(Boolean)
            .map(item => item === 'greatEmpire' ? 'Great Empire' : humanizeLogCode(item))
            .join(', ')

        return {
            time,
            category: 'System',
            tone: 'info',
            summary: `Attack tracking is active in ${kingdomCount} kingdom${kingdomCount === '1' ? '' : 's'}.`,
            detail: appendModuleDetail(prettyKingdoms ? `Watching: ${prettyKingdoms}.` : '', category)
        }
    }

    if (category === 'ReportRewards') {
        const withoutReportId = remaining.replace(/^LID=\S+\s*/i, '')
        const [rewardSection, totalsSection = ''] = withoutReportId.split(/\s+totals\s+/i)
        const rewards = parseLogPairs(rewardSection)
        const totals = parseLogPairs(totalsSection)

        return {
            time,
            category: 'Rewards',
            tone: 'success',
            summary: rewards.length > 0 ?
                `Battle rewards collected: ${formatLogPairList(rewards)}.` :
                'Battle rewards were added to the account.',
            detail: totals.length > 0 ?
                `Total rewards so far: ${formatLogPairList(totals)}.` :
                ''
        }
    }

    if (category === 'Hospital') {
        const healedMatch = remaining.match(/Healed\s+(\d+)\s+troops?/i)
        if (healedMatch) {
            const healed = Number(healedMatch[1])
            return {
                time,
                category: 'Hospital',
                tone: 'success',
                summary: `Hospital healed ${formatLogAmount(healed)} ${healed === 1 ? 'troop' : 'troops'}.`,
                detail: secondaryTag.startsWith('CID:') ? `Castle ${secondaryTag.replace('CID:', '')}.` : ''
            }
        }

        const checkingMatch = remaining.match(/Checking\s+(\d+)\s+hospital slots/i)
        if (checkingMatch) {
            return {
                time,
                category: 'Hospital',
                tone: 'info',
                summary: `Hospital checked ${formatLogAmount(checkingMatch[1])} recovery slots for alliance help.`,
                detail: ''
            }
        }

        const askHelpMatch = remaining.match(/Asking help for slot\s+(\d+)/i)
        if (askHelpMatch) {
            return {
                time,
                category: 'Hospital',
                tone: 'info',
                summary: 'Hospital requested alliance help for a recovery slot.',
                detail: `Slot ${askHelpMatch[1]}.`
            }
        }
    }

    if (category === 'Barracks') {
        if (/Recruitment enabled/i.test(remaining)) {
            return {
                time,
                category: 'Recruit',
                tone: 'info',
                summary: 'Recruit Bot is active and waiting for the next recruitment cycle.',
                detail: appendModuleDetail('', category)
            }
        }

        const recruitedAtMatch = remaining.match(/^Recruited\s+(\d+)\s+troops?\s+at\s+(.+?)\.\s*Unit:\s+(.+?)(?:\.\s*Slots:\s*(\d+))?\.?$/i)
        if (recruitedAtMatch) {
            const [, amount, areaLabel, unitLabel, slotCount] = recruitedAtMatch
            return {
                time,
                category: 'Recruit',
                tone: 'success',
                summary: `Recruit Bot queued ${formatLogAmount(amount)} troops in ${areaLabel}.`,
                detail: [
                    unitLabel ? `Unit: ${unitLabel}.` : '',
                    slotCount ? `Filled ${slotCount} barracks slot${slotCount === '1' ? '' : 's'}.` : ''
                ].filter(Boolean).join(' ')
            }
        }

        const cycleSummaryMatch = remaining.match(/^Cycle complete\.\s*Recruited\s+(\d+)\s+troops?\s+across all castles\.?$/i)
        if (cycleSummaryMatch) {
            return {
                time,
                category: 'Recruit',
                tone: 'success',
                summary: `Recruit Bot queued ${formatLogAmount(cycleSummaryMatch[1])} troops in this cycle.`,
                detail: ''
            }
        }

        if (/^No recruitment started in this cycle\.?$/i.test(remaining)) {
            return {
                time,
                category: 'Recruit',
                tone: 'info',
                summary: 'Recruit Bot checked every configured location, but nothing new was queued this cycle.',
                detail: ''
            }
        }

        const invalidConfigMatch = remaining.match(/^Invalid configuration for area\s+(\d+):\s*(.+)$/i)
        if (invalidConfigMatch) {
            return {
                time,
                category: 'Recruit',
                tone: 'warning',
                summary: 'Recruit Bot skipped one location because the selected troop is not recruitable there right now.',
                detail: `Area ${invalidConfigMatch[1]}. ${invalidConfigMatch[2]}`
            }
        }

        const recruitFailureMatch = remaining.match(/^Recruit request failed:\s*([A-Z0-9_]+)\s*::/i)
        if (recruitFailureMatch) {
            const resultCode = recruitFailureMatch[1]
            return {
                time,
                category: 'Recruit',
                tone: 'warning',
                summary: LOG_RESULT_MESSAGES[resultCode] ?? `Recruit Bot request returned: ${humanizeLogCode(resultCode)}.`,
                detail: appendModuleDetail('Open raw Live Ops if you need the technical payload for this failed request.', category)
            }
        }
    }

    const defenseSendingMatch = remaining.match(/^CastleDefense sending\s+([\d,]+)\/([\d,]+)\s+troop\(s\)\s+from\s+(.+?)\s+to\s+(\d+:\d+)\s+in\s+(\d+)\s+movement\(s\)/i)
    if (defenseSendingMatch) {
        const [, sentTroops, castleTroops, sourceLocation, targetCoords, movementCount] = defenseSendingMatch
        return {
            time,
            category: 'Defense',
            tone: 'defenseSend',
            summary: `Castle Defense is sending ${sentTroops}/${castleTroops} troops from ${sourceLocation} to ${targetCoords}.`,
            detail: `${movementCount} movement${movementCount === '1' ? '' : 's'} created because this castle is under attack.`
        }
    }

    const defenseFleeingMatch = remaining.match(/^Fleeing\s+(.+?)\s+from\s+(\d+:\d+)\s+to\s+(\d+:\d+).*?\bmovement\s+(\d+)\/(\d+).*?\btroops=([\d,]+)/i)
    if (defenseFleeingMatch) {
        const [, sourceLocation, sourceCoords, targetCoords, movementNumber, movementCount, troopCount] = defenseFleeingMatch
        return {
            time,
            category: 'Defense',
            tone: 'defenseSend',
            summary: `Castle Defense sent ${troopCount} troops from ${sourceLocation} ${sourceCoords} to ${targetCoords}.`,
            detail: `Defensive movement ${movementNumber}/${movementCount}.`
        }
    }

    const defenseAcceptedMatch = remaining.match(/^CastleDefense cds accepted movement\s+(\d+)\/(\d+)\s+for\s+([\d,]+)\s+troop\(s\)\s+to\s+(\d+:\d+)/i)
    if (defenseAcceptedMatch) {
        const [, movementNumber, movementCount, troopCount, targetCoords] = defenseAcceptedMatch
        return {
            time,
            category: 'Defense',
            tone: 'defenseSend',
            summary: `Castle Defense confirmed ${troopCount} protected troops to ${targetCoords}.`,
            detail: `Defensive movement ${movementNumber}/${movementCount}.`
        }
    }

    if (/^CastleDefense saw incoming attack/i.test(remaining)) {
        return {
            time,
            category: 'Defense',
            tone: 'defenseSend',
            summary: 'Castle Defense detected an incoming attack.',
            detail: remaining.replace(/^CastleDefense saw incoming attack:\s*/i, '')
        }
    }

    const attackLaunchEntry = parseAttackLaunchLog(remaining, category, time)
    if (attackLaunchEntry)
        return attackLaunchEntry

    if (/Attack template amounts were automatically clamped/i.test(remaining)) {
        return {
            time,
            category: 'Template',
            tone: 'warning',
            summary: 'Template amounts were reduced automatically to fit the target limits.',
            detail: `Module: ${category}.`
        }
    }

    if (/Attack template selections were adjusted/i.test(remaining)) {
        return {
            time,
            category: 'Template',
            tone: 'warning',
            summary: 'Template units and tools were adjusted to what is available in the source castle.',
            detail: `Module: ${category}.`
        }
    }

    const resultMatch = remaining.match(/^Got result\s+([A-Z0-9_]+)\s+from\s+([^\s]+)/i)
    if (resultMatch) {
        const resultCode = resultMatch[1]
        return {
            time,
            category: 'Warning',
            tone: 'warning',
            summary: LOG_RESULT_MESSAGES[resultCode] ?? `Action returned: ${humanizeLogCode(resultCode)}.`,
            detail: `Source: ${resultMatch[2]}.`
        }
    }

    return {
        time,
        category: category.includes('Attack') ? 'Attack' : category,
        tone: remaining.includes('ERROR') ? 'error' : remaining.includes('WARN') ? 'warning' : isAttackLogCategory(category) ? resolveAttackLogTone(category) : 'info',
        summary: remaining.includes('{') && remaining.length > 120 ?
            'Technical activity was recorded for this module.' :
            (remaining || raw),
        detail: remaining.includes('{') && remaining.length > 120 ?
            appendModuleDetail('The raw technical payload is hidden to keep the log readable.', category) :
            appendModuleDetail('', category)
    }
}

const getApprovalSummary = (user, now) => {
    const approvedUntil = Number(user?.approvedUntil)

    if (Boolean(user?.approved)) {
        if (!Number.isFinite(approvedUntil) || approvedUntil <= 0)
            return {
                label: 'Approval permanent',
                color: 'success',
                variant: 'filled',
                sx: {
                    backgroundColor: 'rgba(110, 212, 165, 0.18)',
                    color: '#6ed4a5',
                    border: '1px solid rgba(110, 212, 165, 0.32)'
                }
            }

        const remainingMs = approvedUntil - now
        if (remainingMs <= 0)
            return {
                label: 'Approval expired',
                color: 'error',
                variant: 'filled',
                sx: {
                    backgroundColor: 'rgba(244, 132, 132, 0.18)',
                    color: '#f7a2a2',
                    border: '1px solid rgba(244, 132, 132, 0.34)'
                }
            }

        const threeDaysMs = 3 * 24 * 60 * 60 * 1000
        const sevenDaysMs = 7 * 24 * 60 * 60 * 1000
        const fourteenDaysMs = 14 * 24 * 60 * 60 * 1000

        if (remainingMs <= threeDaysMs) {
            return {
                label: formatApprovalTimeLeft(remainingMs),
                color: 'error',
                variant: 'filled',
                sx: {
                    backgroundColor: 'rgba(244, 132, 132, 0.18)',
                    color: '#f7a2a2',
                    border: '1px solid rgba(244, 132, 132, 0.34)'
                }
            }
        }

        if (remainingMs <= sevenDaysMs) {
            return {
                label: formatApprovalTimeLeft(remainingMs),
                color: 'warning',
                variant: 'filled',
                sx: {
                    backgroundColor: 'rgba(255, 166, 92, 0.18)',
                    color: '#ffbf7a',
                    border: '1px solid rgba(255, 166, 92, 0.34)'
                }
            }
        }

        if (remainingMs <= fourteenDaysMs) {
            return {
                label: formatApprovalTimeLeft(remainingMs),
                color: 'warning',
                variant: 'filled',
                sx: {
                    backgroundColor: 'rgba(245, 212, 110, 0.18)',
                    color: '#f5d46e',
                    border: '1px solid rgba(245, 212, 110, 0.34)'
                }
            }
        }

        return {
            label: formatApprovalTimeLeft(remainingMs),
            color: 'success',
            variant: 'filled',
            sx: {
                backgroundColor: 'rgba(110, 212, 165, 0.18)',
                color: '#6ed4a5',
                border: '1px solid rgba(110, 212, 165, 0.32)'
            }
        }
    }

    if (Number.isFinite(approvedUntil) && approvedUntil > 0 && approvedUntil <= now)
        return {
            label: 'Approval expired',
            color: 'error',
            variant: 'filled',
            sx: {
                backgroundColor: 'rgba(244, 132, 132, 0.18)',
                color: '#f7a2a2',
                border: '1px solid rgba(244, 132, 132, 0.34)'
            }
        }

    return {
        label: 'Not approved',
        color: 'default',
        variant: 'outlined',
        sx: {
            color: '#c8cedd',
            border: '1px solid rgba(255,255,255,0.14)',
            backgroundColor: 'rgba(255,255,255,0.03)'
        }
    }
}

function AttackDetailsDialog({ open, onClose, title, attacks, now, appLanguage = 'en' }) {
    const t = useAutoTranslate(appLanguage, [
        title,
        'Close',
        'Type',
        'Source',
        'Target',
        'Arrival',
        'Impact in',
        'Attack',
        'Return',
        'Support',
        'Siege',
        'Conquer',
        'Spy',
        'Taunt',
        'Unknown',
        'No attacks detected.'
    ])

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(255,255,255,0.06)'
                }}
            >
                <Typography variant="h6">{t(title)}</Typography>
                <Button variant="outlined" onClick={onClose} startIcon={<CloseRoundedIcon />}>
                    {t('Close')}
                </Button>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
                <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('Type')}</TableCell>
                                <TableCell>{t('Source')}</TableCell>
                                <TableCell>{t('Target')}</TableCell>
                                <TableCell>{t('Arrival')}</TableCell>
                                <TableCell>{t('Impact in')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.isArray(attacks) && attacks.length > 0 ? (
                                attacks.map((attack, index) => {
                                    const movement = attack?.movement ?? {}
                                    const source = attack?.sourceCoords ?? (movement?.sourceAttack ? `${movement.sourceAttack.x}:${movement.sourceAttack.y}` : '--')
                                    const target = attack?.targetCoords ?? (movement?.targetAttack ? `${movement.targetAttack.x}:${movement.targetAttack.y}` : '--')
                                    const arrivalTime = resolveAttackEntryArrivalTime(attack, now)
                                    const arrival = Number.isFinite(arrivalTime) ? new Date(arrivalTime).toLocaleTimeString() : 'Unknown'
                                    const impactTimeLeft = formatAttackTimeLeft(arrivalTime, now)

                                    return (
                                        <TableRow key={index}>
                                            <TableCell>{attack?.movementLabel ? t(attack.movementLabel) : (movement.type === 0 ? t('Attack') : String(movement.type ?? t('Unknown')))}</TableCell>
                                            <TableCell>{source}</TableCell>
                                            <TableCell>{target}</TableCell>
                                            <TableCell>{arrival}</TableCell>
                                            <TableCell>{impactTimeLeft}</TableCell>
                                        </TableRow>
                                    )
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                        {t('No attacks detected.')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
        </Dialog>
    )
}

function AllianceChatDialog({ open, onClose, messages, user, ws, appLanguage = 'en' }) {
    const [draft, setDraft] = React.useState('')
    const [translationLanguage, setTranslationLanguage] = React.useState(appLanguage || 'en')
    const [translatedMessages, setTranslatedMessages] = React.useState([])
    const [translationPending, setTranslationPending] = React.useState(false)
    const [translationError, setTranslationError] = React.useState('')
    const scrollRef = React.useRef(null)
    const t = useAutoTranslate(appLanguage, [
        'Alliance chat',
        'Real-time alliance chat',
        'Translate to',
        'Close',
        'Original',
        'Showing original alliance messages.',
        'No alliance messages yet.',
        'Send message',
        'Send',
        'Unknown',
        'Original:',
        'Translation is unavailable right now. Showing original chat.'
    ])
    const selectedLanguageLabel = React.useMemo(
        () => getChatLanguageLabel(translationLanguage),
        [translationLanguage]
    )

    React.useEffect(() => {
        if (!open)
            setDraft('')
    }, [open])

    React.useEffect(() => {
        setTranslationLanguage(appLanguage || 'en')
    }, [appLanguage, open])

    React.useEffect(() => {
        if (!open)
            return

        if (translationLanguage === 'original') {
            setTranslatedMessages([])
            setTranslationPending(false)
            setTranslationError('')
            return
        }

        const sourceMessages = Array.isArray(messages) ? messages : []
        if (sourceMessages.length === 0) {
            setTranslatedMessages([])
            setTranslationPending(false)
            setTranslationError('')
            return
        }

        let ignore = false
        const controller = new AbortController()

        const loadTranslations = async () => {
            setTranslationPending(true)
            setTranslationError('')

            try {
                const response = await fetch('/translate-chat', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        targetLanguage: translationLanguage,
                        messages: sourceMessages
                    }),
                    signal: controller.signal
                })

                if (!response.ok)
                    throw new Error(`Translation request failed (${response.status})`)

                const payload = await response.json()
                if (ignore)
                    return

                setTranslatedMessages(Array.isArray(payload?.messages) ? payload.messages : [])
                setTranslationError(typeof payload?.error === 'string' ? payload.error : '')
            }
            catch (error) {
                if (ignore || error?.name === 'AbortError')
                    return

                setTranslatedMessages([])
                setTranslationError('Translation is unavailable right now. Showing original chat.')
            }
            finally {
                if (!ignore)
                    setTranslationPending(false)
            }
        }

        loadTranslations()

        return () => {
            ignore = true
            controller.abort()
        }
    }, [messages, open, translationLanguage])

    React.useEffect(() => {
        if (!open)
            return

        const panel = scrollRef.current
        if (panel)
            panel.scrollTop = panel.scrollHeight
    }, [messages, open])

    const renderedMessages = React.useMemo(() => {
        const sourceMessages = Array.isArray(messages) ? messages : []
        return sourceMessages.map((message, index) => {
            const originalText = String(message?.messageText ?? '')
            const translatedText = translationLanguage === 'original' ?
                originalText :
                String(translatedMessages[index]?.translatedText ?? originalText)

            return {
                ...message,
                originalText,
                renderedText: translatedText || originalText,
                showOriginal: translationLanguage !== 'original' && translatedText !== originalText
            }
        })
    }, [messages, translatedMessages, translationLanguage])

    const handleSend = () => {
        const text = draft.trim()
        if (!text || !user)
            return

        ws.send(JSON.stringify([
            ErrorType.Success,
            ActionType.SendAllianceChat,
            { user, message: text }
        ]))
        setDraft('')
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle
                sx={{
                    borderBottom: '1px solid rgba(255,255,255,0.06)'
                }}
            >
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', md: 'center' }}
                >
                    <Box>
                        <Typography variant="h6">{t('Alliance chat')}</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {user?.name ? `${user.name} alliance messages in real time` : t('Real-time alliance chat')}
                        </Typography>
                    </Box>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.25}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                    >
                        <TextField
                            select
                            size="small"
                            label={t('Translate to')}
                            value={translationLanguage}
                            onChange={event => setTranslationLanguage(event.target.value)}
                            sx={{ minWidth: { xs: '100%', sm: 260 } }}
                        >
                            <MenuItem value="original">{t('Original')}</MenuItem>
                            {CHAT_LANGUAGE_OPTIONS.map(option => (
                                <MenuItem key={option.code} value={option.code}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Button variant="outlined" onClick={onClose} startIcon={<CloseRoundedIcon />}>
                            {t('Close')}
                        </Button>
                    </Stack>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ p: 2.5 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    sx={{ mb: 1.5 }}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        {translationPending ? (
                            <CircularProgress size={18} />
                        ) : (
                            <TranslateRoundedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        )}
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {translationLanguage === 'original' ?
                                t('Showing original alliance messages.') :
                                `Auto translating chat to ${selectedLanguageLabel}.`}
                        </Typography>
                    </Stack>
                    {translationError && (
                        <Typography variant="caption" sx={{ color: '#f2b36f' }}>
                            {translationError}
                        </Typography>
                    )}
                </Stack>

                <Box
                    ref={scrollRef}
                    sx={{
                        minHeight: 420,
                        maxHeight: '58vh',
                        overflow: 'auto',
                        p: 2,
                        borderRadius: 2.5,
                        backgroundColor: '#0d1522',
                        border: '1px solid rgba(255,255,255,0.06)'
                    }}
                >
                    {renderedMessages.length > 0 ? (
                        renderedMessages.map((message, index) => (
                            <Box
                                key={`${message?.playerID ?? 'player'}-${message?.sentAt ?? index}-${index}`}
                                sx={{
                                    py: 1,
                                    borderBottom: '1px solid rgba(255,255,255,0.05)'
                                }}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
                                    <Typography variant="body2" sx={{ color: '#7bddb0', fontWeight: 700 }}>
                                        {message?.playerName || 'Unknown'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                                        {formatChatTime(message?.sentAt)}
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {message.renderedText}
                                </Typography>
                                {message.showOriginal && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            mt: 0.75,
                                            display: 'block',
                                            color: 'text.secondary',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word'
                                        }}
                                    >
                                        {t('Original:')} {message.originalText}
                                    </Typography>
                                )}
                            </Box>
                        ))
                    ) : (
                        <Typography sx={{ color: 'text.secondary' }}>
                            {t('No alliance messages yet.')}
                        </Typography>
                    )}
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 2 }}>
                    <TextField
                        fullWidth
                        label={t('Send message')}
                        value={draft}
                        onChange={event => setDraft(event.target.value)}
                        onKeyDown={event => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault()
                                handleSend()
                            }
                        }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<SendRoundedIcon />}
                        onClick={handleSend}
                        sx={{ minWidth: { xs: '100%', sm: 150 } }}
                    >
                        {t('Send')}
                    </Button>
                </Stack>
            </DialogContent>
        </Dialog>
    )
}

function ResourceBar({ label, value, gain, color }) {
    const hasGain = typeof gain === 'number' && gain > 0

    return (
        <Box sx={{ mb: 1.15 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.55 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {label}
                </Typography>
                <Stack direction="row" spacing={1}>
                    {hasGain && (
                        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700 }}>
                            +{formatCompact(gain)}
                        </Typography>
                    )}
                    <Typography variant="caption" sx={{ color, fontWeight: 700 }}>
                        {formatFull(value)}
                    </Typography>
                </Stack>
            </Stack>
            <LinearProgress
                variant="determinate"
                value={100}
                sx={{
                    height: 6,
                    '& .MuiLinearProgress-bar': {
                        backgroundColor: color
                    }
                }}
            />
        </Box>
    )
}

function ReportTotalsChips({ totals, codes, appLanguage = 'en' }) {
    const t = useAutoTranslate(appLanguage, [
        'Attack gains',
        'Rubies',
        'Coins',
        'Aquamarine',
        'Food',
        'Wood',
        'Stone',
        'Coal',
        'Oil',
        'Glass',
        'Samurai medals',
        'Samurai points',
        'Samurai tokens'
    ])
    const knownResources = [
        { code: 'C2', key: 'rubies', label: 'Rubies' },
        { code: 'C1', key: 'coins', label: 'Coins' },
        { code: 'A', key: 'aquamarine', label: 'Aquamarine' },
        { code: 'F', key: 'food', label: 'Food' },
        { code: 'W', key: 'wood', label: 'Wood' },
        { code: 'S', key: 'stone', label: 'Stone' },
        { code: 'C', key: 'coal', label: 'Coal' },
        { code: 'O', key: 'oil', label: 'Oil' },
        { code: 'G', key: 'glass', label: 'Glass' },
        { code: 'SP', key: 'shogunPoints', label: 'Samurai medals' },
        { code: 'SM', key: 'samuraiPoints', label: 'Samurai points' },
        { code: 'ST', key: 'samuraiTokens', label: 'Samurai tokens' }
    ].filter(item => typeof totals?.[item.key] === 'number' && totals[item.key] > 0)

    const ignoredCodes = new Set(['C1', 'C2', 'A', 'F', ...knownResources.map(item => item.code)])
    const extraResources = Object.entries(codes || {})
        .map(([code, value]) => ({ code, value: Number(value) }))
        .filter(item => Number.isFinite(item.value) && item.value > 0 && !ignoredCodes.has(item.code))

    if (knownResources.length === 0 && extraResources.length === 0)
        return null

    return (
        <Box sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: 'text.secondary' }}>
                {t('Attack gains')}
            </Typography>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                {knownResources.map(item => (
                    <Chip key={item.key} size="small" label={`${t(item.label)} +${formatFull(totals[item.key])}`} />
                ))}
                {extraResources.map(item => (
                    <Chip key={item.code} size="small" label={`${item.code} +${formatFull(item.value)}`} />
                ))}
            </Stack>
        </Box>
    )
}

function SkipInventoryPanel({ inventory }) {
    const skipTypes = [
        ['MS1', '1m'],
        ['MS2', '5m'],
        ['MS3', '10m'],
        ['MS4', '30m'],
        ['MS5', '1h'],
        ['MS6', '5h'],
        ['MS7', '24h']
    ]

    return (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
            {skipTypes.map(([code, label]) => (
                <Chip key={code} size="small" label={`${label} - ${formatSkipAmount(inventory?.[code])}`} />
            ))}
        </Stack>
    )
}

function SummaryCard({ title, value, caption, icon, color }) {
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.25 }}>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {title}
                        </Typography>
                        <Typography variant="h5" sx={{ mt: 0.75 }}>
                            {value}
                        </Typography>
                        <Typography variant="caption" sx={{ mt: 0.75, display: 'block', color: 'text.secondary' }}>
                            {caption}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2.5,
                            display: 'grid',
                            placeItems: 'center',
                            backgroundColor: `${color}22`,
                            color
                        }}
                    >
                        {icon}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    )
}

// eslint-disable-next-line no-unused-vars
function DailyAttackLimitSummaryCard({ users, statusMap, appLanguage = 'en' }) {
    const [selectedUserId, setSelectedUserId] = React.useState('')
    const [selectedModuleKey, setSelectedModuleKey] = React.useState('')
    const t = useAutoTranslate(appLanguage, [
        'Daily attacks',
        'Account',
        'Module',
        'No attack counters available yet.',
        'Attack counters appear after the first successful hit.',
        'Attack limit',
        'Remaining today',
        'Tracked by bot today',
        'Configured stop',
        'Last sync',
        'Threshold reached today'
    ])

    const attackStatusByUser = React.useMemo(
        () => (Array.isArray(users) ? users : [])
            .map(user => {
                const rawEntries =
                    statusMap?.[user?.id]?.dailyAttackLimits &&
                        typeof statusMap[user.id].dailyAttackLimits === 'object' ?
                        statusMap[user.id].dailyAttackLimits :
                        {}

                const entries = Object.entries(rawEntries)
                    .map(([moduleKey, entry]) => {
                        const rawAttackCount = Number(entry?.attackCount ?? 0)
                        const attackCount =
                            Number.isFinite(rawAttackCount) && rawAttackCount > 0 ?
                                Math.floor(rawAttackCount) :
                                0
                        const serverLimit = Math.max(
                            1,
                            Math.floor(getFirstPositiveFiniteNumber(entry?.serverLimit, entry?.effectiveThreshold, 3500) ?? 3500)
                        )
                        const rawTrackedAttackCount = Number(entry?.trackedAttackCount)
                        const trackedAttackCount =
                            entry?.trackedAttackCount === undefined || entry?.trackedAttackCount === null ?
                                undefined :
                                Number.isFinite(rawTrackedAttackCount) && rawTrackedAttackCount >= 0 ?
                                    Math.floor(rawTrackedAttackCount) :
                                    0
                        const effectiveThreshold = Math.max(
                            1,
                            Math.floor(getFirstPositiveFiniteNumber(entry?.effectiveThreshold, entry?.configuredThreshold, serverLimit) ?? serverLimit)
                        )

                        return {
                            moduleKey,
                            label: String(entry?.label ?? moduleKey),
                            attackCount,
                            trackedAttackCount,
                            serverLimit,
                            effectiveThreshold,
                            reached: Boolean(entry?.reached),
                            updatedAt: Number(entry?.updatedAt ?? NaN)
                        }
                    })
                    .sort((left, right) => String(left.label ?? '').localeCompare(String(right.label ?? '')))

                return {
                    userId: Number(user?.id),
                    userName: String(user?.name ?? t('Account')),
                    entries
                }
            })
            .filter(entry => entry.entries.length > 0)
            .sort((left, right) => String(left.userName ?? '').localeCompare(String(right.userName ?? ''))),
        [statusMap, t, users]
    )

    React.useEffect(() => {
        if (attackStatusByUser.length === 0) {
            if (selectedUserId)
                setSelectedUserId('')
            if (selectedModuleKey)
                setSelectedModuleKey('')
            return
        }

        if (!attackStatusByUser.some(entry => String(entry.userId) === String(selectedUserId)))
            setSelectedUserId(String(attackStatusByUser[0].userId))
    }, [attackStatusByUser, selectedModuleKey, selectedUserId])

    const selectedUserEntry = attackStatusByUser.find(entry => String(entry.userId) === String(selectedUserId)) ?? attackStatusByUser[0]

    React.useEffect(() => {
        if (!selectedUserEntry) {
            if (selectedModuleKey)
                setSelectedModuleKey('')
            return
        }

        if (!selectedUserEntry.entries.some(entry => entry.moduleKey === selectedModuleKey))
            setSelectedModuleKey(selectedUserEntry.entries[0]?.moduleKey ?? '')
    }, [selectedModuleKey, selectedUserEntry])

    const selectedEntry = selectedUserEntry?.entries.find(entry => entry.moduleKey === selectedModuleKey) ?? selectedUserEntry?.entries[0]
    const selectedAttackCount = Number(selectedEntry?.attackCount ?? 0)
    const attackCount =
        Number.isFinite(selectedAttackCount) && selectedAttackCount > 0 ?
            Math.floor(selectedAttackCount) :
            0
    const serverLimit = Math.max(1, Math.floor(Number(selectedEntry?.serverLimit ?? 3500)))
    const remainingToday = Math.max(0, serverLimit - attackCount)
    const progressValue = Math.max(0, Math.min(100, (attackCount / serverLimit) * 100))

    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.25 }}>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('Daily attacks')}
                        </Typography>
                        <Typography variant="h5" sx={{ mt: 0.75 }}>
                            {selectedEntry ? formatFull(attackCount) : '0'}
                        </Typography>

                        {attackStatusByUser.length > 1 ? (
                            <TextField
                                select
                                size="small"
                                fullWidth
                                label={t('Account')}
                                value={selectedUserEntry?.userId ?? ''}
                                onChange={event => setSelectedUserId(String(event.target.value))}
                                sx={{ mt: 1.25 }}
                            >
                                {attackStatusByUser.map(entry => (
                                    <MenuItem key={entry.userId} value={entry.userId}>
                                        {entry.userName}
                                    </MenuItem>
                                ))}
                            </TextField>
                        ) : (
                            <Typography variant="caption" sx={{ mt: 0.9, display: 'block', color: 'text.secondary' }}>
                                {selectedUserEntry?.userName ?? t('No attack counters available yet.')}
                            </Typography>
                        )}

                        {selectedUserEntry?.entries?.length > 1 && (
                            <TextField
                                select
                                size="small"
                                fullWidth
                                label={t('Module')}
                                value={selectedEntry?.moduleKey ?? ''}
                                onChange={event => setSelectedModuleKey(String(event.target.value))}
                                sx={{ mt: 1.1 }}
                            >
                                {selectedUserEntry.entries.map(entry => (
                                    <MenuItem key={entry.moduleKey} value={entry.moduleKey}>
                                        {entry.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}

                        {selectedEntry ? (
                            <Box sx={{ mt: 1.1 }}>
                                {selectedUserEntry?.entries?.length <= 1 && (
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                        {selectedEntry.label}
                                    </Typography>
                                )}
                                <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mt: 0.6 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {`${t('Attack limit')}: ${formatFull(serverLimit)}`}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {`${Math.round(progressValue)}%`}
                                    </Typography>
                                </Stack>
                                <LinearProgress
                                    variant="determinate"
                                    value={progressValue}
                                    sx={{
                                        mt: 0.75,
                                        height: 6,
                                        borderRadius: 999,
                                        backgroundColor: 'rgba(111, 157, 255, 0.18)',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 999,
                                            backgroundColor: selectedEntry.reached ? '#f48484' : '#6f9dff'
                                        }
                                    }}
                                />
                                <Typography variant="caption" sx={{ mt: 0.75, display: 'block', color: 'text.secondary' }}>
                                    {`${t('Remaining today')}: ${formatFull(remainingToday)}`}
                                </Typography>
                                {selectedEntry.trackedAttackCount !== undefined && (
                                    <Typography variant="caption" sx={{ mt: 0.45, display: 'block', color: 'text.secondary' }}>
                                        {`${t('Tracked by bot today')}: ${formatFull(selectedEntry.trackedAttackCount)}`}
                                    </Typography>
                                )}
                                <Typography variant="caption" sx={{ mt: 0.45, display: 'block', color: 'text.secondary' }}>
                                    {`${t('Configured stop')}: ${formatFull(selectedEntry.effectiveThreshold)}`}
                                </Typography>
                                <Typography variant="caption" sx={{ mt: 0.45, display: 'block', color: 'text.secondary' }}>
                                    {selectedEntry.reached ?
                                        t('Threshold reached today') :
                                        `${t('Last sync')}: ${formatChatTime(selectedEntry.updatedAt)}`}
                                </Typography>
                            </Box>
                        ) : (
                            <Typography variant="caption" sx={{ mt: 0.9, display: 'block', color: 'text.secondary' }}>
                                {attackStatusByUser.length > 0 ?
                                    t('Attack counters appear after the first successful hit.') :
                                    t('No attack counters available yet.')}
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2.5,
                            display: 'grid',
                            placeItems: 'center',
                            backgroundColor: 'rgba(111, 157, 255, 0.14)',
                            color: '#6f9dff'
                        }}
                    >
                        <SendRoundedIcon />
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    )
}

function UnderAttackSummaryCard({ users, statusMap, now, appLanguage = 'en' }) {
    const [selectedAttackId, setSelectedAttackId] = React.useState('')
    const t = useAutoTranslate(appLanguage, [
        'Under attack',
        'Tracked attack',
        'Attacked account',
        'Attacker',
        'Target',
        'Arrival',
        'Status',
        'Active',
        'Cleared',
        'Last seen',
        'Unknown',
        'Recent attackers saved.',
        'No active attacks right now.',
        'Attack details are syncing...'
    ])

    const attackEntries = React.useMemo(
        () => (Array.isArray(users) ? users : [])
            .flatMap(user => {
                const userStatus = statusMap?.[user?.id] ?? {}
                const incomingAttacks = Array.isArray(userStatus?.incomingAttacks) ?
                    userStatus.incomingAttacks :
                    []
                const recentIncomingAttackers = Array.isArray(userStatus?.recentIncomingAttackers) ?
                    userStatus.recentIncomingAttackers :
                    []
                const activeKeys = new Set()
                const buildKey = attack => {
                    const movementId = Number(attack?.movementId)
                    if (Number.isFinite(movementId))
                        return `movement:${movementId}`

                    const storedId = String(attack?.id ?? '').trim()
                    if (storedId)
                        return storedId

                    return [
                        attack?.attackerId ?? attack?.sourceOwnerId ?? attack?.attackerName,
                        attack?.sourceCoords,
                        attack?.targetCoords,
                        attack?.arrivalTime ?? attack?.lastArrivalTime
                    ].filter(value => value !== undefined && value !== null && String(value).trim() !== '').join(':')
                }
                const buildEntry = (attack, index, active) => {
                    const arrivalTime = resolveAttackEntryArrivalTime(attack, now)
                    const key = buildKey(attack) || `${active ? 'active' : 'recent'}:${index}`

                    if (active)
                        activeKeys.add(key)

                    return {
                        id: [
                            user?.id ?? 'user',
                            active ? 'active' : 'recent',
                            key,
                            index
                        ].join(':'),
                        key,
                        userId: Number(user?.id),
                        accountName: String(user?.name ?? t('Unknown')),
                        attackerName: String(attack?.attackerName ?? t('Unknown')),
                        targetCoords: String(attack?.targetCoords ?? '--'),
                        arrivalTime,
                        lastSeenAt: Number(attack?.lastSeenAt ?? userStatus?.lastIncomingAttackAt ?? NaN),
                        active
                    }
                }

                const activeEntries = incomingAttacks.map((attack, index) => buildEntry(attack, index, true))
                const recentEntries = recentIncomingAttackers
                    .map((attack, index) => buildEntry(attack, index, Boolean(attack?.active)))
                    .filter(entry => !activeKeys.has(entry.key))

                return [...activeEntries, ...recentEntries]
            })
            .sort((left, right) =>
                Number(Boolean(right.active)) - Number(Boolean(left.active)) ||
                Number(left.arrivalTime ?? Number.MAX_SAFE_INTEGER) - Number(right.arrivalTime ?? Number.MAX_SAFE_INTEGER) ||
                Number(right.lastSeenAt ?? 0) - Number(left.lastSeenAt ?? 0) ||
                String(left.accountName ?? '').localeCompare(String(right.accountName ?? '')) ||
                String(left.attackerName ?? '').localeCompare(String(right.attackerName ?? ''))),
        [now, statusMap, t, users]
    )

    const totalIncomingAttacks = React.useMemo(
        () => (Array.isArray(users) ? users : []).reduce(
            (sum, user) => sum + Number(statusMap?.[user?.id]?.incomingAttackCount ?? 0),
            0
        ),
        [statusMap, users]
    )

    React.useEffect(() => {
        if (attackEntries.length === 0) {
            if (selectedAttackId)
                setSelectedAttackId('')
            return
        }

        if (!attackEntries.some(entry => entry.id === selectedAttackId))
            setSelectedAttackId(attackEntries[0].id)
    }, [attackEntries, selectedAttackId])

    const selectedAttack = attackEntries.find(entry => entry.id === selectedAttackId) ?? attackEntries[0]

    return (
        <Card
            sx={{
                height: '100%',
                borderColor: totalIncomingAttacks > 0 ? 'rgba(248,113,113,0.72)' : undefined,
                backgroundColor: totalIncomingAttacks > 0 ? 'rgba(127,29,29,0.18)' : undefined,
                transition: 'background-color 220ms ease, border-color 220ms ease'
            }}
        >
            <CardContent sx={{ p: 2.25 }}>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('Under attack')}
                        </Typography>
                        <Typography variant="h5" sx={{ mt: 0.75 }}>
                            {formatCompact(totalIncomingAttacks)}
                        </Typography>
                        {totalIncomingAttacks === 0 && attackEntries.length > 0 ? (
                            <Typography variant="caption" sx={{ mt: 0.35, display: 'block', color: 'text.secondary' }}>
                                {t('Recent attackers saved.')}
                            </Typography>
                        ) : null}

                        {attackEntries.length > 1 && (
                            <TextField
                                select
                                size="small"
                                fullWidth
                                label={t('Tracked attack')}
                                value={selectedAttack?.id ?? ''}
                                onChange={event => setSelectedAttackId(event.target.value)}
                                sx={{ mt: 1.25 }}
                            >
                                {attackEntries.map(entry => (
                                    <MenuItem key={entry.id} value={entry.id}>
                                        {buildAttackSelectionLabel(entry)}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}

                        {selectedAttack ? (
                            <Box sx={{ mt: 1.1 }}>
                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                    {`${t('Attacked account')}: ${selectedAttack.accountName}`}
                                </Typography>
                                <Typography variant="caption" sx={{ mt: 0.45, display: 'block', color: 'text.secondary' }}>
                                    {`${t('Attacker')}: ${selectedAttack.attackerName}`}
                                </Typography>
                                <Typography variant="caption" sx={{ mt: 0.45, display: 'block', color: 'text.secondary' }}>
                                    {`${t('Target')}: ${selectedAttack.targetCoords}`}
                                </Typography>
                                <Typography variant="caption" sx={{ mt: 0.45, display: 'block', color: 'text.secondary' }}>
                                    {`${t('Status')}: ${selectedAttack.active ? t('Active') : t('Cleared')}`}
                                </Typography>
                                <Typography variant="caption" sx={{ mt: 0.45, display: 'block', color: 'text.secondary' }}>
                                    {selectedAttack.active ?
                                        `${t('Arrival')}: ${formatAttackTimeLeft(selectedAttack.arrivalTime, now)}` :
                                        `${t('Last seen')}: ${formatAttackLastSeen(selectedAttack.lastSeenAt)}`}
                                </Typography>
                            </Box>
                        ) : (
                            <Typography variant="caption" sx={{ mt: 0.9, display: 'block', color: 'text.secondary' }}>
                                {totalIncomingAttacks > 0 ?
                                    t('Attack details are syncing...') :
                                    t('No active attacks right now.')}
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2.5,
                            display: 'grid',
                            placeItems: 'center',
                            backgroundColor: 'rgba(244, 132, 132, 0.14)',
                            color: '#f48484'
                        }}
                    >
                        <WarningAmberRoundedIcon />
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    )
}

// eslint-disable-next-line no-unused-vars
function UserCard({ user, status, plugins, ws, onOpenSettings, onOpenLogs, onOpenChat, now, appLanguage = 'en' }) {
    const [active, setActive] = React.useState(Boolean(user.state))
    const [detailsType, setDetailsType] = React.useState(null)
    const t = useAutoTranslate(appLanguage, [
        'Power',
        'No UUID',
        'Active',
        'Offline',
        'Under attack',
        'Incoming',
        'Outgoing',
        'Attacks on me',
        'Top',
        'Division ranking',
        'Season ranking',
        'Uptime',
        'Resources',
        'Rubies',
        'Unknown',
        'Unavailable',
        'Coins',
        'Aquamarine',
        'Food',
        'Mead',
        'Active modules',
        'No active modules',
        'Settings',
        'Logs',
        'Chat',
        'Stop',
        'Start',
        'Incoming commanders',
        'Outgoing commanders'
    ])

    React.useEffect(() => setActive(Boolean(user.state)), [user.state])

    const incoming = Number(status?.incomingCommanderCount ?? 0)
    const outgoing = Number(status?.outgoingCommanderCount ?? 0)
    const attacksOnMe = Number(status?.incomingAttackCount ?? 0)
    const leagueDivisionRank = Number(status?.leagueDivisionRank)
    const leagueSeasonRank = Number(status?.leagueSeasonRank)
    const leagueDivisionId = Number(status?.leagueDivisionId)
    const leagueDivisionSize = Number(status?.leagueDivisionSize)
    const divisionRankingLabel =
        Number.isFinite(leagueDivisionRank) && leagueDivisionRank > 0 ?
            `${Number.isFinite(leagueDivisionId) && leagueDivisionId > 0 ? `Division ${formatPlainWholeNumber(leagueDivisionId)} | ` : ''}#${formatPlainWholeNumber(leagueDivisionRank)}${Number.isFinite(leagueDivisionSize) && leagueDivisionSize > 0 ? `/${formatPlainWholeNumber(leagueDivisionSize)}` : ''}` :
            t('Unavailable')
    const seasonRankingLabel =
        Number.isFinite(leagueSeasonRank) && leagueSeasonRank > 0 ?
            `#${formatPlainWholeNumber(leagueSeasonRank)}` :
            t('Unavailable')
    const visiblePlugins = Array.isArray(plugins) ? plugins.filter(plugin => !plugin?.hidden) : []
    const activePlugins = Object.entries(user.plugins || {}).filter(([, value]) => value?.state)
    const activePluginNames = activePlugins
        .map(([key]) => visiblePlugins.find(plugin => plugin.key === key)?.name)
        .filter(Boolean)
    const shownPluginNames = activePluginNames.slice(0, 4)
    const hiddenPluginCount = Math.max(0, activePluginNames.length - shownPluginNames.length)
    const underAttack = Boolean(status?.isUnderAttack)
    const effectiveNow = Number.isFinite(Number(now)) ? Number(now) : Date.now()
    const approvalSummary = getApprovalSummary(user, effectiveNow)
    const accountPower = Number(status?.mightPoints)
    const allianceName = String(status?.allianceName ?? '').trim()
    const hasAccountPower = Number.isFinite(accountPower) && accountPower > 0
    const canStart = Boolean(user?.approved)
    const showPrimaryAction = active || canStart

    const toggleState = () => {
        const newState = !active
        setActive(newState)
        user.state = newState ? 1 : 0
        ws.send(JSON.stringify([ErrorType.Success, ActionType.SetUser, user]))
    }

    return (
        <Card sx={{ height: '100%', borderColor: underAttack ? 'rgba(244,132,132,0.28)' : undefined }}>
            <CardContent sx={{ p: 2.25 }}>
                <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                        <Avatar sx={{ bgcolor: active ? 'primary.main' : 'rgba(255,255,255,0.08)', color: active ? 'primary.contrastText' : 'text.primary' }}>
                            {String(user?.name ?? '?').slice(0, 1).toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ minWidth: 0 }}>
                                <Typography variant="h6" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {user.name}
                                </Typography>
                                {hasAccountPower && (
                                    <Typography
                                        variant="caption"
                                        title={`${formatFull(accountPower)} power`}
                                        sx={{
                                            color: '#d7b768',
                                            fontWeight: 700,
                                            whiteSpace: 'nowrap',
                                            flexShrink: 0
                                        }}
                                    >
                                        {`${formatCompact(accountPower)} ${t('Power')}`}
                                    </Typography>
                                )}
                            </Stack>
                            {allianceName && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                >
                                    {allianceName}
                                </Typography>
                            )}
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {user.uuid?.substring(0, 8) || t('No UUID')}
                            </Typography>
                        </Box>
                    </Stack>

                    <Chip
                        size="small"
                        label={active ? t('Active') : t('Offline')}
                        color={active ? 'primary' : 'default'}
                        variant={active ? 'filled' : 'outlined'}
                    />
                </Stack>

                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                    <Chip
                        size="small"
                        label={approvalSummary.label}
                        color={approvalSummary.color}
                        variant={approvalSummary.variant}
                        sx={approvalSummary.sx}
                    />
                </Stack>

                {underAttack && (
                    <Box
                        sx={{
                            mt: 1.75,
                            p: 1,
                            borderRadius: 2.5,
                            backgroundColor: 'rgba(244,132,132,0.08)',
                            border: '1px solid rgba(244,132,132,0.22)'
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <WarningAmberRoundedIcon sx={{ color: 'error.main', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700 }}>
                                {t('Under attack')}
                            </Typography>
                        </Stack>
                    </Box>
                )}

                <Grid container spacing={1.2} sx={{ mt: 1.2 }}>
                    <Grid item xs={6}>
                        <Box sx={{ p: 1.2, borderRadius: 2.5, backgroundColor: 'rgba(255,255,255,0.03)' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('Incoming')}</Typography>
                            <Typography variant="body1" sx={{ mt: 0.35, fontWeight: 700 }}>{formatCompact(incoming)}</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box sx={{ p: 1.2, borderRadius: 2.5, backgroundColor: 'rgba(255,255,255,0.03)' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('Outgoing')}</Typography>
                            <Typography variant="body1" sx={{ mt: 0.35, fontWeight: 700 }}>{formatCompact(outgoing)}</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box sx={{ p: 1.2, borderRadius: 2.5, backgroundColor: 'rgba(255,255,255,0.03)' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('Attacks on me')}</Typography>
                            <Typography variant="body1" sx={{ mt: 0.35, fontWeight: 700 }}>{formatFull(attacksOnMe)}</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box sx={{ p: 1.2, borderRadius: 2.5, backgroundColor: 'rgba(255,255,255,0.03)' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('Uptime')}</Typography>
                            <Typography variant="body1" sx={{ mt: 0.35, fontWeight: 700 }}>
                                {formatDuration(resolveRuntimeUptimeMs(status, effectiveNow))}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ p: 1.2, borderRadius: 2.5, backgroundColor: 'rgba(255,255,255,0.03)' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('Top')}</Typography>
                            <Grid container spacing={1} sx={{ mt: 0.35 }}>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ p: 1, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.025)' }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {t('Division ranking')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.25, fontWeight: 700 }}>
                                            {divisionRankingLabel}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ p: 1, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.025)' }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {t('Season ranking')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.25, fontWeight: 700 }}>
                                            {seasonRankingLabel}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                        {t('Resources')}
                    </Typography>
                    <ResourceBar
                        label={t('Samurai medals')}
                        value={getFirstFiniteNumber(
                            status?.samuraiPoints,
                            status?.shogunPoints,
                            status?.reportRewardTotals?.samuraiPoints > 0 ? status.reportRewardTotals.samuraiPoints : undefined,
                            status?.reportRewardTotals?.shogunPoints > 0 ? status.reportRewardTotals.shogunPoints : undefined
                        ) ?? t('Unknown')}
                        gain={getFirstPositiveFiniteNumber(
                            status?.reportRewardTotals?.samuraiPoints,
                            status?.reportRewardTotals?.shogunPoints
                        )}
                        color="#e6b85b"
                    />
                    <ResourceBar
                        label={t('Samurai tokens')}
                        value={getFirstFiniteNumber(
                            status?.samuraiTokens,
                            status?.reportRewardTotals?.samuraiTokens > 0 ? status.reportRewardTotals.samuraiTokens : undefined,
                            status?.reportRewardCodes?.ST > 0 ? status.reportRewardCodes.ST : undefined
                        ) ?? t('Unknown')}
                        gain={getFirstPositiveFiniteNumber(
                            status?.reportRewardTotals?.samuraiTokens,
                            status?.reportRewardCodes?.ST
                        )}
                        color="#8fd3ff"
                    />
                    <ResourceBar label={t('Rubies')} value={status?.rubies ?? t('Unknown')} gain={status?.earnedRubies} color="#f2a7cc" />
                    <ResourceBar label={t('Coins')} value={status?.coin ?? t('Unknown')} gain={status?.earnedCoins} color="#ecd79f" />
                    <ResourceBar label={t('Aquamarine')} value={status?.aquamarine ?? t('Unknown')} gain={status?.earnedAquamarine} color="#73c9f1" />
                    <ResourceBar label={t('Food')} value={status?.food ?? t('Unknown')} gain={status?.earnedFood} color="#6ed4a5" />
                    <ResourceBar label={t('Mead')} value={status?.mead ?? t('Unknown')} color="#d0a48b" />
                    <ReportTotalsChips totals={status?.reportRewardTotals} codes={status?.reportRewardCodes} appLanguage={appLanguage} />
                    <SkipInventoryPanel inventory={status?.skipInventory} />
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                        {t('Active modules')}
                    </Typography>
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                        {shownPluginNames.length > 0 ? shownPluginNames.map(name => (
                            <Chip key={name} size="small" label={name} />
                        )) : (
                            <Chip size="small" label={t('No active modules')} />
                        )}
                        {hiddenPluginCount > 0 && (
                            <Chip size="small" label={`+${hiddenPluginCount} more`} />
                        )}
                    </Stack>
                </Box>

                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
                    <Chip
                        size="small"
                        icon={<SecurityRoundedIcon sx={{ fontSize: 16 }} />}
                        label={`${t('Incoming')} ${incoming}`}
                        onClick={() => setDetailsType('incoming')}
                    />
                    <Chip
                        size="small"
                        icon={<SendRoundedIcon sx={{ fontSize: 16 }} />}
                        label={`${t('Outgoing')} ${outgoing}`}
                        onClick={() => setDetailsType('outgoing')}
                    />
                    <Chip
                        size="small"
                        icon={<ReceiptLongRoundedIcon sx={{ fontSize: 16 }} />}
                        label={`Hits ${formatCompact(Number(status?.sentHits ?? 0))}`}
                    />
                    <Chip
                        size="small"
                        icon={<AccessTimeRoundedIcon sx={{ fontSize: 16 }} />}
                        label={formatDuration(resolveRuntimeUptimeMs(status, effectiveNow))}
                    />
                </Stack>
            </CardContent>

            <CardActions sx={{ px: 2.25, pb: 2.25, pt: 0, justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Button variant="outlined" startIcon={<SettingsRoundedIcon />} onClick={onOpenSettings}>
                        {t('Settings')}
                    </Button>
                    <Button variant="outlined" startIcon={<ReceiptLongRoundedIcon />} onClick={onOpenLogs}>
                        {t('Logs')}
                    </Button>
                    <Button variant="outlined" startIcon={<ChatRoundedIcon />} onClick={onOpenChat}>
                        {t('Chat')}
                    </Button>
                </Stack>
                {showPrimaryAction && (
                    <Button
                        variant={active ? 'outlined' : 'contained'}
                        color={active ? 'error' : 'primary'}
                        startIcon={active ? <StopRoundedIcon /> : <PlayArrowRoundedIcon />}
                        onClick={toggleState}
                    >
                        {active ? t('Stop') : t('Start')}
                    </Button>
                )}
            </CardActions>

            <AttackDetailsDialog
                open={Boolean(detailsType)}
                onClose={() => setDetailsType(null)}
                title={detailsType === 'incoming' ? 'Incoming commanders' : 'Outgoing commanders'}
                attacks={detailsType === 'incoming' ? status?.incomingCommanders : status?.outgoingCommanders}
                now={effectiveNow}
                appLanguage={appLanguage}
            />
        </Card>
    )
}

function LogDialog({ open, onClose, logs, userName, appLanguage = 'en' }) {
    const logViewportRef = React.useRef(null)
    const panelRef = React.useRef(null)
    const dragRef = React.useRef(null)
    const [position, setPosition] = React.useState({ x: 24, y: 24 })
    const messages = React.useMemo(
        () => normalizeLocalizedLogMessages(logs?.buffer),
        [logs?.buffer]
    )
    const friendlyEntries = React.useMemo(
        () => messages.map(buildFriendlyLogEntry).filter(Boolean).slice(-50),
        [messages]
    )
    const t = useAutoTranslate(appLanguage, [
        `${userName} logs`,
        'Close',
        'No readable activity yet.',
        'Technical battle packets are hidden automatically.',
        ...friendlyEntries.flatMap(entry => [
            entry.category,
            entry.summary,
            entry.detail
        ].filter(Boolean))
    ])

    React.useEffect(() => {
        if (!open || !logViewportRef.current)
            return

        logViewportRef.current.scrollTop = logViewportRef.current.scrollHeight
    }, [friendlyEntries.length, open])

    React.useLayoutEffect(() => {
        if (!open || !panelRef.current)
            return

        const panelBounds = panelRef.current.getBoundingClientRect()
        setPosition(current => ({
            x: Math.max(8, Math.min(current.x, window.innerWidth - panelBounds.width - 8)),
            y: Math.max(8, Math.min(current.y, window.innerHeight - panelBounds.height - 8))
        }))
    }, [open])

    React.useEffect(() => {
        if (!open)
            return undefined

        const handlePointerMove = event => {
            const drag = dragRef.current
            const panel = panelRef.current
            if (!drag || !panel)
                return

            const bounds = panel.getBoundingClientRect()
            setPosition({
                x: Math.max(8, Math.min(event.clientX - drag.offsetX, window.innerWidth - bounds.width - 8)),
                y: Math.max(8, Math.min(event.clientY - drag.offsetY, window.innerHeight - bounds.height - 8))
            })
        }
        const handlePointerUp = () => {
            dragRef.current = null
        }
        const keepPanelOnScreen = () => {
            const panel = panelRef.current
            if (!panel)
                return
            const bounds = panel.getBoundingClientRect()
            setPosition(current => ({
                x: Math.max(8, Math.min(current.x, window.innerWidth - bounds.width - 8)),
                y: Math.max(8, Math.min(current.y, window.innerHeight - bounds.height - 8))
            }))
        }

        window.addEventListener('pointermove', handlePointerMove)
        window.addEventListener('pointerup', handlePointerUp)
        window.addEventListener('pointercancel', handlePointerUp)
        window.addEventListener('resize', keepPanelOnScreen)
        return () => {
            window.removeEventListener('pointermove', handlePointerMove)
            window.removeEventListener('pointerup', handlePointerUp)
            window.removeEventListener('pointercancel', handlePointerUp)
            window.removeEventListener('resize', keepPanelOnScreen)
        }
    }, [open])

    const beginDrag = event => {
        if (event.button !== 0 || event.target.closest('button'))
            return
        const bounds = panelRef.current?.getBoundingClientRect()
        if (!bounds)
            return
        dragRef.current = {
            offsetX: event.clientX - bounds.left,
            offsetY: event.clientY - bounds.top
        }
        event.preventDefault()
    }

    if (!open)
        return null

    return (
        <Portal>
            <Box sx={{ position: 'fixed', inset: 0, zIndex: theme => theme.zIndex.modal + 1, pointerEvents: 'none' }}>
                <Paper
                    ref={panelRef}
                    role="dialog"
                    aria-label={t(`${userName} logs`)}
                    sx={{
                        position: 'fixed',
                        left: position.x,
                        top: position.y,
                        width: 'min(760px, calc(100vw - 16px))',
                        height: 'min(760px, calc(100vh - 16px))',
                        minWidth: 'min(320px, calc(100vw - 16px))',
                        minHeight: 'min(260px, calc(100vh - 16px))',
                        maxWidth: 'calc(100vw - 16px)',
                        maxHeight: 'calc(100vh - 16px)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        resize: 'both',
                        pointerEvents: 'auto',
                        border: '1px solid rgba(255,255,255,0.12)',
                        boxShadow: '0 24px 70px rgba(0,0,0,0.55)'
                    }}
                >
            <DialogTitle
                onPointerDown={beginDrag}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    cursor: 'move',
                    userSelect: 'none',
                    touchAction: 'none'
                }}
            >
                <Typography variant="h6">{t(`${userName} logs`)}</Typography>
                <Button variant="outlined" onClick={onClose} startIcon={<CloseRoundedIcon />}>
                    {t('Close')}
                </Button>
            </DialogTitle>
            <DialogContent sx={{ p: 2.5, display: 'flex', minHeight: 0, flex: 1, flexDirection: 'column' }}>
                <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                    {t('Technical battle packets are hidden automatically.')}
                </Typography>
                <Box
                    ref={logViewportRef}
                    sx={{
                        minHeight: 0,
                        flex: 1,
                        overflow: 'auto',
                        p: 2,
                        borderRadius: 2.5,
                        backgroundColor: '#0d1522',
                        border: '1px solid rgba(255,255,255,0.06)'
                    }}
                >
                    {friendlyEntries.length === 0 ? (
                        <Typography sx={{ color: 'text.secondary' }}>{t('No readable activity yet.')}</Typography>
                    ) : (
                        friendlyEntries.map((entry, index) => {
                            const tone = getLogToneStyles(entry.tone)

                            return (
                                <Box
                                    key={index}
                                    sx={{
                                        p: 1.35,
                                        mb: 1,
                                        borderRadius: 2.5,
                                        border: `1px solid ${tone.borderColor}`,
                                        borderLeft: `4px solid ${tone.accentColor ?? tone.borderColor}`,
                                        backgroundColor: tone.backgroundColor,
                                        boxShadow: tone.accentColor ? `0 0 0 1px ${tone.borderColor}, 0 10px 28px rgba(245, 158, 11, 0.13)` : 'none'
                                    }}
                                >
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
                                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                            {entry.time && (
                                                <Chip size="small" variant="outlined" label={entry.time} />
                                            )}
                                            <Chip
                                                size="small"
                                                label={t(entry.category)}
                                                sx={{
                                                    backgroundColor: `${tone.chipColor}20`,
                                                    color: tone.chipColor,
                                                    border: `1px solid ${tone.chipColor}55`
                                                }}
                                            />
                                        </Stack>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            #{index}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 700, color: tone.summaryColor ?? 'text.primary', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {t(entry.summary)}
                                    </Typography>
                                    {entry.detail && (
                                        <Typography variant="caption" sx={{ mt: 0.75, display: 'block', color: 'text.secondary', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {t(entry.detail)}
                                        </Typography>
                                    )}
                                </Box>
                            )
                        })
                    )}
                </Box>
            </DialogContent>
                <Box
                    aria-hidden="true"
                    sx={{
                        position: 'absolute',
                        right: 3,
                        bottom: 1,
                        color: 'text.secondary',
                        fontSize: 17,
                        lineHeight: 1,
                        pointerEvents: 'none',
                        opacity: 0.85
                    }}
                >
                    ◢
                </Box>
                </Paper>
            </Box>
        </Portal>
    )
}

export default function UserDashboard({ ws, rows, moderationUsers, usersStatus, plugins, channelInfo, logs, allianceChat, isAdmin, accountInfo, liveNow, appLanguage = 'en', onAppLanguageChange }) {
    const users = React.useMemo(() => Array.isArray(rows) ? rows : [], [rows])
    const moderatorRows = React.useMemo(() => Array.isArray(moderationUsers) ? moderationUsers : [], [moderationUsers])
    const statusMap = React.useMemo(() => usersStatus || {}, [usersStatus])
    const [searchTerm, setSearchTerm] = React.useState('')
    const deferredSearchTerm = React.useDeferredValue(searchTerm)
    const [botLayout, setBotLayout] = React.useState('panel')
    const groupStorageKey = React.useMemo(() => getDashboardBotGroupStorageKey(accountInfo), [accountInfo])
    const [botGroupStore, setBotGroupStore] = React.useState(() => ({
        key: groupStorageKey,
        groups: readDashboardBotGroups(groupStorageKey)
    }))
    const [expandedGroupIds, setExpandedGroupIds] = React.useState({ [DASHBOARD_UNGROUPED_GROUP_ID]: true })
    const [draggingGroupId, setDraggingGroupId] = React.useState('')
    const [dragOverGroup, setDragOverGroup] = React.useState(null)
    const [selectedUser, setSelectedUser] = React.useState(null)
    const [settingsOpen, setSettingsOpen] = React.useState(false)
    const [logSubscriptionUser, setLogSubscriptionUser] = React.useState(null)
    const [logDialogUser, setLogDialogUser] = React.useState(null)
    const [chatUser, setChatUser] = React.useState(null)
    const [view, setView] = React.useState('dashboard')
    const t = useAutoTranslate(appLanguage, [
        'Online bots',
        'Under attack',
        'Active modules',
        'Khan horns',
        'Khan attacks (30m)',
        'Active now',
        `${users.length} total bots`,
        'Bots that need attention',
        'Enabled modules across all bots',
        'Incoming Khan taunts across all bots',
        'No Khan horns active right now',
        'Your Bots',
        'Manage your bots and view bots shared with you',
        'Search bots by name...',
        'Create Bot',
        'Create Group',
        'Your Ungrouped Bots',
        'Ungrouped',
        'Bot group',
        'Group name',
        'Delete group',
        'Rename group',
        'Move group',
        'Move group up',
        'Move group down',
        'No bots in this group',
        'Use the group selector on a bot card to place it here.',
        'No bots found',
        'Try a different search or create a new bot.',
        'Unknown'
    ])

    const effectiveNow = useSmoothLiveNow(liveNow)

    const normalizedRole = String(accountInfo?.role ?? '').trim().toLowerCase()
    const canAccessModeratorView = Boolean(accountInfo?.isModerator || normalizedRole === 'moderator')
    const botGroups = botGroupStore.groups

    React.useEffect(() => {
        setBotGroupStore(currentStore => {
            if (currentStore.key === groupStorageKey)
                return currentStore

            return {
                key: groupStorageKey,
                groups: readDashboardBotGroups(groupStorageKey)
            }
        })
    }, [groupStorageKey])

    React.useEffect(() => {
        if (botGroupStore.key !== groupStorageKey)
            return

        persistDashboardBotGroups(botGroupStore.key, botGroupStore.groups)
    }, [botGroupStore, groupStorageKey])

    React.useEffect(() => {
        if (isAdmin || !canAccessModeratorView)
            return

        setView(currentView => currentView === 'dashboard' ? 'moderator' : currentView)
    }, [canAccessModeratorView, isAdmin])

    const updateBotGroups = React.useCallback(updater => {
        setBotGroupStore(currentStore => {
            const baseGroups = currentStore.key === groupStorageKey ? currentStore.groups : readDashboardBotGroups(groupStorageKey)
            const nextGroups = typeof updater === 'function' ? updater(baseGroups) : updater

            return {
                key: groupStorageKey,
                groups: normalizeDashboardBotGroups(nextGroups)
            }
        })
    }, [groupStorageKey])

    const groupView = React.useMemo(
        () => buildDashboardBotGroupView(users, botGroups, deferredSearchTerm),
        [botGroups, deferredSearchTerm, users]
    )

    const getAssignedGroupId = React.useCallback(user => {
        const userId = getDashboardBotId(user)
        const assignedGroup = botGroups.find(group => group.userIds.includes(userId))
        return assignedGroup?.id ?? DASHBOARD_UNGROUPED_GROUP_ID
    }, [botGroups])

    const isGroupExpanded = React.useCallback(groupId =>
        groupId === DASHBOARD_UNGROUPED_GROUP_ID ?
            expandedGroupIds[groupId] !== false :
            expandedGroupIds[groupId] === true,
    [expandedGroupIds])

    const toggleGroupExpanded = React.useCallback(groupId => {
        setExpandedGroupIds(current => {
            const currentlyExpanded = groupId === DASHBOARD_UNGROUPED_GROUP_ID ?
                current[groupId] !== false :
                current[groupId] === true

            return {
                ...current,
                [groupId]: !currentlyExpanded
            }
        })
    }, [])

    const handleCreateGroup = React.useCallback(() => {
        const groupId = createDashboardBotGroupId()
        updateBotGroups(currentGroups => [
            ...currentGroups,
            {
                id: groupId,
                name: buildNextDashboardGroupName(currentGroups),
                userIds: []
            }
        ])
        setExpandedGroupIds(current => ({ ...current, [groupId]: true }))
    }, [updateBotGroups])

    const handleRenameGroup = React.useCallback((groupId, nextName) => {
        updateBotGroups(currentGroups =>
            currentGroups.map(group =>
                group.id === groupId ?
                    { ...group, name: normalizeDashboardGroupName(nextName, group.name || 'Group') } :
                    group
            )
        )
    }, [updateBotGroups])

    const handleDeleteGroup = React.useCallback(groupId => {
        const group = botGroups.find(candidate => candidate.id === groupId)
        if (!group)
            return

        if (!window.confirm(`${t('Delete group')}: ${group.name}?`))
            return

        updateBotGroups(currentGroups => currentGroups.filter(candidate => candidate.id !== groupId))
        setExpandedGroupIds(current => {
            const next = { ...current }
            delete next[groupId]
            return next
        })
    }, [botGroups, t, updateBotGroups])

    const handleMoveGroup = React.useCallback((groupId, offset) => {
        updateBotGroups(currentGroups => moveDashboardBotGroup(currentGroups, groupId, offset))
    }, [updateBotGroups])

    const handleGroupDragStart = React.useCallback((event, groupId) => {
        setDraggingGroupId(groupId)
        setDragOverGroup(null)
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', groupId)
    }, [])

    const handleGroupDragOver = React.useCallback((event, groupId) => {
        const sourceGroupId = draggingGroupId || event.dataTransfer.getData('text/plain')
        if (!sourceGroupId || sourceGroupId === groupId)
            return

        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'

        const bounds = event.currentTarget.getBoundingClientRect()
        const placement = event.clientY > bounds.top + bounds.height / 2 ? 'after' : 'before'
        setDragOverGroup(current =>
            current?.id === groupId && current?.placement === placement ?
                current :
                { id: groupId, placement }
        )
    }, [draggingGroupId])

    const handleGroupDrop = React.useCallback((event, targetGroupId) => {
        event.preventDefault()
        const sourceGroupId = draggingGroupId || event.dataTransfer.getData('text/plain')
        const bounds = event.currentTarget.getBoundingClientRect()
        const pointerPlacement = event.clientY > bounds.top + bounds.height / 2 ? 'after' : 'before'
        const placement = dragOverGroup?.id === targetGroupId ? dragOverGroup.placement : pointerPlacement

        if (sourceGroupId && sourceGroupId !== targetGroupId)
            updateBotGroups(currentGroups => reorderDashboardBotGroups(currentGroups, sourceGroupId, targetGroupId, placement))

        setDraggingGroupId('')
        setDragOverGroup(null)
    }, [dragOverGroup, draggingGroupId, updateBotGroups])

    const handleGroupDragEnd = React.useCallback(() => {
        setDraggingGroupId('')
        setDragOverGroup(null)
    }, [])

    const handleAssignUserToGroup = React.useCallback((user, nextGroupId) => {
        const userId = getDashboardBotId(user)
        if (!userId)
            return

        updateBotGroups(currentGroups =>
            currentGroups.map(group => {
                const userIdsWithoutCurrent = group.userIds.filter(currentUserId => currentUserId !== userId)

                if (group.id !== nextGroupId)
                    return { ...group, userIds: userIdsWithoutCurrent }

                return {
                    ...group,
                    userIds: [...userIdsWithoutCurrent, userId]
                }
            })
        )

        if (nextGroupId !== DASHBOARD_UNGROUPED_GROUP_ID)
            setExpandedGroupIds(current => ({ ...current, [nextGroupId]: true }))
    }, [updateBotGroups])

    const activeUsers = users.filter(user => Boolean(user?.state)).length
    const activeModules = users.reduce((sum, user) =>
        sum + getDashboardActivePluginNames(user?.plugins, plugins, user?.botSettings).length, 0)
    const incomingKhanTauntCount = users.reduce((sum, user) =>
        sum + resolveIncomingKhanTauntCount(statusMap?.[user?.id]),
    0)
    const recentKhanAttackCount = users.reduce((sum, user) =>
        sum + resolveRecentKhanAttackCount(statusMap?.[user?.id]),
    0)

    const botGridProps = botLayout === 'grid'
        ? { xs: 12, sm: 6, lg: 4, xl: 3 }
        : botLayout === 'list'
            ? { xs: 12 }
            : { xs: 12, md: 6, xl: 4 }

    const overview = [
        {
            title: t('Online bots'),
            value: formatCompact(activeUsers),
            caption: t(`${users.length} total bots`),
            icon: <BoltRoundedIcon />,
            color: '#6f9dff'
        },
        {
            title: t('Active modules'),
            value: formatCompact(activeModules),
            caption: t('Enabled modules across all bots'),
            icon: <DiamondRoundedIcon />,
            color: '#d7b768'
        },
        {
            title: t('Khan attacks (30m)'),
            value: formatCompact(recentKhanAttackCount),
            caption: `${t('Active now')}: ${formatCompact(incomingKhanTauntCount)}`,
            icon: <WarningAmberRoundedIcon />,
            color: '#f48484'
        }
    ]

    const handleOpenSettings = user => {
        setSelectedUser(user)
        setSettingsOpen(true)
    }

    const handleCreateUser = () => {
        setSelectedUser({})
        setSettingsOpen(true)
    }

    const handleOpenLogs = (user, { openDialog = true } = {}) => {
        if (!user)
            return

        setLogSubscriptionUser(user)
        setLogDialogUser(previous => openDialog ? user : previous)
        ws.send(JSON.stringify([ErrorType.Success, ActionType.GetLogs, user]))
    }

    const handleCloseLogs = ({ closeDialog = true } = {}) => {
        ws.send(JSON.stringify([ErrorType.Success, ActionType.GetLogs, null]))
        setLogSubscriptionUser(null)

        if (closeDialog)
            setLogDialogUser(null)
    }

    React.useEffect(() => {
        if (!logSubscriptionUser)
            return undefined

        const subscribeLogs = () => {
            try {
                ws.send(JSON.stringify([ErrorType.Success, ActionType.GetLogs, logSubscriptionUser]))
            } catch (error) {
                console.warn('[LiveOpsRealtime] Failed to subscribe logs', error)
            }
        }

        subscribeLogs()
        ws.addEventListener('open', subscribeLogs)
        return () => ws.removeEventListener('open', subscribeLogs)
    }, [logSubscriptionUser, ws])

    React.useEffect(() => {
        if (!chatUser)
            return undefined

        const subscribeChat = () => {
            try {
                ws.send(JSON.stringify([ErrorType.Success, ActionType.GetAllianceChat, chatUser]))
            } catch (error) {
                console.warn('[AllianceChatRealtime] Failed to subscribe chat', error)
            }
        }

        subscribeChat()
        ws.addEventListener('open', subscribeChat)
        return () => ws.removeEventListener('open', subscribeChat)
    }, [chatUser, ws])

    const handleOpenChat = user => {
        setChatUser(user)
        ws.send(JSON.stringify([ErrorType.Success, ActionType.GetAllianceChat, user]))
    }

    const handleRemoveUser = user => {
        if (!user)
            return

        if (!window.confirm(`Delete "${user.name}"?`))
            return

        ws.send(JSON.stringify([ErrorType.Success, ActionType.RemoveUser, [user]]))
    }

    const handleCloseChat = () => {
        ws.send(JSON.stringify([ErrorType.Success, ActionType.GetAllianceChat, null]))
        setChatUser(null)
    }

    const safeSetView = nextView => {
        if (nextView === 'admin' && !isAdmin)
            return

        if (nextView === 'moderator' && !canAccessModeratorView)
            return

        setView(nextView)
    }

    const renderBotCard = user => (
        <Grid item key={getDashboardBotId(user) || user.id} {...botGridProps}>
            <Stack spacing={1}>
                {botGroups.length > 0 && (
                    <TextField
                        select
                        size="small"
                        label={t('Bot group')}
                        value={getAssignedGroupId(user)}
                        onChange={event => handleAssignUserToGroup(user, event.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <FolderRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                </InputAdornment>
                            )
                        }}
                        sx={{
                            maxWidth: { xs: '100%', sm: 260 },
                            '& .MuiInputBase-root': {
                                borderRadius: 2,
                                backgroundColor: 'rgba(255,255,255,0.05)'
                            }
                        }}
                    >
                        <MenuItem value={DASHBOARD_UNGROUPED_GROUP_ID}>{t('Ungrouped')}</MenuItem>
                        {botGroups.map(group => (
                            <MenuItem key={group.id} value={group.id}>
                                {group.name || t('Group name')}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
                <DashboardBotCard
                    user={user}
                    status={statusMap[user.id] || {}}
                    plugins={plugins}
                    ws={ws}
                    now={effectiveNow}
                    layout={botLayout}
                    onOpenSettings={() => handleOpenSettings(user)}
                    onOpenLogs={() => handleOpenLogs(user)}
                    onOpenChat={() => handleOpenChat(user)}
                    onDelete={handleRemoveUser}
                    appLanguage={appLanguage}
                />
            </Stack>
        </Grid>
    )

    const renderGroupBody = (sectionUsers, emptyTitle, emptyCaption) => (
        <Box sx={{ p: 2 }}>
            {sectionUsers.length === 0 ? (
                <Card>
                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="h6">{emptyTitle}</Typography>
                        <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary' }}>
                            {emptyCaption}
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <Grid container spacing={2.5}>
                    {sectionUsers.map(user => renderBotCard(user))}
                </Grid>
            )}
        </Box>
    )

    return (
        <MainLayout
            currentView={view}
            onViewChange={safeSetView}
            isAdmin={isAdmin}
            canAccessModeratorView={canAccessModeratorView}
            accountInfo={accountInfo}
            liveNow={effectiveNow}
            appLanguage={appLanguage}
            onAppLanguageChange={onAppLanguageChange}
        >
            {view === 'admin' && isAdmin ? (
                <AdminPage
                    ws={ws}
                    users={users}
                    onClose={() => setView('dashboard')}
                    accountInfo={accountInfo}
                    liveNow={effectiveNow}
                    appLanguage={appLanguage}
                />
            ) : view === 'moderator' && canAccessModeratorView ? (
                <AdminPage
                    ws={ws}
                    users={moderatorRows}
                    onClose={() => setView('dashboard')}
                    accountInfo={accountInfo}
                    liveNow={effectiveNow}
                    appLanguage={appLanguage}
                    mode="moderator"
                />
            ) : view === 'attack-templates' ? (
                <AttackTemplatesPage
                    accountInfo={accountInfo}
                    users={users}
                    usersStatus={statusMap}
                    appLanguage={appLanguage}
                />
            ) : view === 'battle-reports' ? (
                <BattleReportsPanel users={users} />
            ) : view === 'pricing' ? (
                <PricingPage accountInfo={accountInfo} appLanguage={appLanguage} />
            ) : (
                <Box sx={{ display: 'grid', gap: 2.5 }}>
                    <EventCalendar appLanguage={appLanguage} users={users} usersStatus={statusMap} />
                    <Stack spacing={1}>
                        <Typography variant="h3" sx={{ fontSize: { xs: '2rem', md: '2.35rem' }, letterSpacing: '-0.04em' }}>
                            {t('Your Bots')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 680 }}>
                            {t('Manage your bots and view bots shared with you')}
                        </Typography>
                    </Stack>

                    <Stack
                        direction={{ xs: 'column', lg: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', lg: 'center' }}
                        justifyContent="space-between"
                    >
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                                p: 0.75,
                                borderRadius: 3,
                                width: 'fit-content',
                                backgroundColor: 'rgba(255,255,255,0.05)',
                                border: '1px solid rgba(255,255,255,0.08)'
                            }}
                        >
                            {[
                                { key: 'panel', icon: <ViewAgendaRoundedIcon /> },
                                { key: 'grid', icon: <GridViewRoundedIcon /> },
                                { key: 'list', icon: <ViewListRoundedIcon /> }
                            ].map(option => {
                                const active = botLayout === option.key

                                return (
                                    <IconButton
                                        key={option.key}
                                        onClick={() => setBotLayout(option.key)}
                                        sx={{
                                            width: 38,
                                            height: 38,
                                            borderRadius: 2.5,
                                            color: active ? 'text.primary' : 'text.secondary',
                                            backgroundColor: active ? 'rgba(255,255,255,0.12)' : 'transparent',
                                            border: active ? '1px solid rgba(255,255,255,0.14)' : '1px solid transparent',
                                            '&:hover': {
                                                backgroundColor: active ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.08)'
                                            }
                                        }}
                                    >
                                        {option.icon}
                                    </IconButton>
                                )
                            })}
                        </Box>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                            <Button
                                variant="contained"
                                startIcon={<AddRoundedIcon />}
                                onClick={handleCreateGroup}
                                sx={{
                                    minWidth: { xs: '100%', sm: 178 },
                                    backgroundColor: 'rgba(255,255,255,0.11)',
                                    color: 'text.primary',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.16)',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                {t('Create Group')}
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<AddRoundedIcon />}
                                onClick={handleCreateUser}
                                sx={{
                                    minWidth: { xs: '100%', sm: 168 },
                                    backgroundColor: 'rgba(255,255,255,0.11)',
                                    color: 'text.primary',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.16)',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                {t('Create Bot')}
                            </Button>
                        </Stack>
                    </Stack>

                    <Stack spacing={1.5}>
                        {groupView.groups.map((group, groupIndex) => {
                            const expanded = isGroupExpanded(group.id)
                            const countText = group.users.length === group.allUsers.length ?
                                String(group.users.length) :
                                `${group.users.length}/${group.allUsers.length}`
                            const dragPlacement = dragOverGroup?.id === group.id ? dragOverGroup.placement : ''
                            const isDragging = draggingGroupId === group.id

                            return (
                                <Box
                                    key={group.id}
                                    onDragOver={event => handleGroupDragOver(event, group.id)}
                                    onDrop={event => handleGroupDrop(event, group.id)}
                                    sx={{
                                        borderRadius: 3,
                                        overflow: 'hidden',
                                        backgroundColor: 'rgba(255,255,255,0.04)',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        borderTopColor: dragPlacement === 'before' ? '#60a5fa' : 'rgba(255,255,255,0.08)',
                                        borderBottomColor: dragPlacement === 'after' ? '#60a5fa' : 'rgba(255,255,255,0.08)',
                                        borderTopWidth: dragPlacement === 'before' ? 2 : 1,
                                        borderBottomWidth: dragPlacement === 'after' ? 2 : 1,
                                        opacity: isDragging ? 0.64 : 1,
                                        transition: 'border-color 160ms ease, opacity 160ms ease'
                                    }}
                                >
                                    <Stack
                                        direction={{ xs: 'column', md: 'row' }}
                                        spacing={1}
                                        alignItems={{ xs: 'stretch', md: 'center' }}
                                        sx={{
                                            p: 1,
                                            backgroundColor: 'rgba(70, 116, 196, 0.18)'
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={0.5}
                                            alignItems="center"
                                            sx={{
                                                alignSelf: { xs: 'flex-start', md: 'center' },
                                                flexShrink: 0
                                            }}
                                        >
                                            <Tooltip title={t('Move group')}>
                                                <IconButton
                                                    draggable
                                                    onDragStart={event => handleGroupDragStart(event, group.id)}
                                                    onDragEnd={handleGroupDragEnd}
                                                    sx={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: 2,
                                                        color: '#bfdbfe',
                                                        cursor: 'grab',
                                                        '&:active': { cursor: 'grabbing' },
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(147,197,253,0.12)'
                                                        }
                                                    }}
                                                >
                                                    <DragIndicatorRoundedIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={t('Move group up')}>
                                                <span>
                                                    <IconButton
                                                        disabled={groupIndex === 0}
                                                        onClick={() => handleMoveGroup(group.id, -1)}
                                                        sx={{
                                                            width: 36,
                                                            height: 36,
                                                            borderRadius: 2,
                                                            color: '#dbeafe',
                                                            '&.Mui-disabled': {
                                                                color: 'rgba(219,234,254,0.28)'
                                                            },
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(147,197,253,0.12)'
                                                            }
                                                        }}
                                                    >
                                                        <KeyboardArrowUpRoundedIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={t('Move group down')}>
                                                <span>
                                                    <IconButton
                                                        disabled={groupIndex >= groupView.groups.length - 1}
                                                        onClick={() => handleMoveGroup(group.id, 1)}
                                                        sx={{
                                                            width: 36,
                                                            height: 36,
                                                            borderRadius: 2,
                                                            color: '#dbeafe',
                                                            '&.Mui-disabled': {
                                                                color: 'rgba(219,234,254,0.28)'
                                                            },
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(147,197,253,0.12)'
                                                            }
                                                        }}
                                                    >
                                                        <KeyboardArrowDownRoundedIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Stack>

                                        <Button
                                            onClick={() => toggleGroupExpanded(group.id)}
                                            sx={{
                                                flex: 1,
                                                px: 1.25,
                                                py: 1,
                                                justifyContent: 'space-between',
                                                color: 'text.primary',
                                                textTransform: 'none',
                                                borderRadius: 2,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,255,255,0.06)'
                                                }
                                            }}
                                        >
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                                <FolderRoundedIcon sx={{ fontSize: 20, color: '#93c5fd' }} />
                                                <Typography variant="h6" sx={{ fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {`${group.name} (${countText})`}
                                                </Typography>
                                            </Stack>
                                            {expanded ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
                                        </Button>

                                        <TextField
                                            size="small"
                                            label={t('Group name')}
                                            defaultValue={group.name}
                                            onBlur={event => handleRenameGroup(group.id, event.target.value)}
                                            onKeyDown={event => {
                                                if (event.key === 'Enter')
                                                    event.currentTarget.blur()
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <DriveFileRenameOutlineRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                    </InputAdornment>
                                                )
                                            }}
                                            sx={{
                                                width: { xs: '100%', md: 240 },
                                                '& .MuiInputBase-root': {
                                                    borderRadius: 2,
                                                    backgroundColor: 'rgba(255,255,255,0.05)'
                                                }
                                            }}
                                        />

                                        <Tooltip title={t('Delete group')}>
                                            <IconButton
                                                onClick={() => handleDeleteGroup(group.id)}
                                                sx={{
                                                    alignSelf: { xs: 'flex-end', md: 'center' },
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 2,
                                                    color: '#fca5a5',
                                                    border: '1px solid rgba(252,165,165,0.3)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(239,68,68,0.16)'
                                                    }
                                                }}
                                            >
                                                <DeleteRoundedIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>

                                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                                        {renderGroupBody(
                                            group.users,
                                            t('No bots in this group'),
                                            t('Use the group selector on a bot card to place it here.')
                                        )}
                                    </Collapse>
                                </Box>
                            )
                        })}

                        <Box
                            sx={{
                                borderRadius: 3,
                                overflow: 'hidden',
                                backgroundColor: 'rgba(255,255,255,0.04)',
                                border: '1px solid rgba(255,255,255,0.08)'
                            }}
                        >
                            <Button
                                fullWidth
                                onClick={() => toggleGroupExpanded(DASHBOARD_UNGROUPED_GROUP_ID)}
                                sx={{
                                    px: 2,
                                    py: 1.4,
                                    justifyContent: 'space-between',
                                    color: 'text.primary',
                                    textTransform: 'none',
                                    borderRadius: 0,
                                    backgroundColor: 'rgba(109, 137, 179, 0.28)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(109, 137, 179, 0.34)'
                                    }
                                }}
                            >
                                <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                                    {`${t('Your Ungrouped Bots')} (${groupView.ungroupedUsers.length})`}
                                </Typography>
                                {isGroupExpanded(DASHBOARD_UNGROUPED_GROUP_ID) ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
                            </Button>

                            <Collapse in={isGroupExpanded(DASHBOARD_UNGROUPED_GROUP_ID)} timeout="auto" unmountOnExit>
                                {renderGroupBody(
                                    groupView.ungroupedUsers,
                                    t('No bots found'),
                                    t('Try a different search or create a new bot.')
                                )}
                            </Collapse>
                        </Box>
                    </Stack>

                    <Card>
                        <CardContent sx={{ p: { xs: 2, md: 2.25 } }}>
                            <TextField
                                fullWidth
                                placeholder={t('Search bots by name...')}
                                value={searchTerm}
                                onChange={event => React.startTransition(() => setSearchTerm(event.target.value))}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchRoundedIcon sx={{ color: 'text.secondary' }} />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </CardContent>
                    </Card>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} xl={3}>
                            <SummaryCard {...overview[0]} />
                        </Grid>
                        <Grid item xs={12} sm={6} xl={3}>
                            <UnderAttackSummaryCard
                                users={users}
                                statusMap={statusMap}
                                now={effectiveNow}
                                appLanguage={appLanguage}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} xl={3}>
                            <SummaryCard {...overview[1]} />
                        </Grid>
                        <Grid item xs={12} sm={6} xl={3}>
                            <SummaryCard {...overview[2]} />
                        </Grid>
                    </Grid>
                </Box>
            )}

            <Drawer
                anchor="right"
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(6, 10, 18, 0.96)',
                        backdropFilter: 'blur(10px)'
                    }
                }}
                PaperProps={{
                    sx: {
                        width: '100vw',
                        maxWidth: '100vw',
                        height: '100vh',
                        top: 0,
                        borderRadius: 0,
                        backgroundImage: 'none',
                        backgroundColor: 'rgba(13, 20, 35, 1)'
                    }
                }}
            >
                {selectedUser && (
                    <SettingsDrawer
                        selectedUser={selectedUser}
                        onBotCreated={setSelectedUser}
                        selectedUserStatus={selectedUser ? (statusMap[selectedUser.id] || {}) : {}}
                        plugins={plugins}
                        channels={channelInfo?.[1]}
                        ws={ws}
                        onClose={() => setSettingsOpen(false)}
                        onStartLiveOps={() => handleOpenLogs(selectedUser, { openDialog: false })}
                        onStopLiveOps={() => handleCloseLogs({ closeDialog: false })}
                        logs={selectedUser && logSubscriptionUser && Number(logSubscriptionUser.id) === Number(selectedUser.id) ? logs : null}
                        liveOpsActive={Boolean(selectedUser && logSubscriptionUser && Number(logSubscriptionUser.id) === Number(selectedUser.id))}
                        accountInfo={accountInfo}
                        currentView={view}
                        isAdmin={isAdmin}
                        appLanguage={appLanguage}
                        onAppLanguageChange={onAppLanguageChange}
                        onViewChange={safeSetView}
                    />
                )}
            </Drawer>

            <LogDialog
                open={Boolean(logDialogUser)}
                onClose={() => handleCloseLogs({ closeDialog: true })}
                logs={logs}
                userName={logDialogUser?.name || t('Unknown')}
                appLanguage={appLanguage}
            />

            <AllianceChatDialog
                open={Boolean(chatUser)}
                onClose={handleCloseChat}
                messages={allianceChat}
                user={chatUser}
                ws={ws}
                appLanguage={appLanguage}
            />
        </MainLayout>
    )
}
